<template>
  <v-container fluid class="pa-6">
    <!-- KPI Cards Section -->
    <v-row class="mb-6">
      <v-col cols="12" sm="6" md="3">
        <v-card class="kpi-card elevation-1" :loading="loadingStats">
          <v-card-text>
            <div class="kpi-icon">
              <v-icon color="primary">mdi-file-document-multiple</v-icon>
            </div>
            <div class="kpi-content">
              <div class="kpi-label">Documentos Procesados</div>
              <div class="kpi-value">{{ stats.total_documents.toLocaleString() }}</div>
              <div class="kpi-subtitle">Este mes</div>
            </div>
          </v-card-text>
        </v-card>
      </v-col>
      <v-col cols="12" sm="6" md="3">
        <v-card class="kpi-card elevation-1" :loading="loadingStats">
          <v-card-text>
            <div class="kpi-icon">
              <v-icon color="success">mdi-package-variant</v-icon>
            </div>
            <div class="kpi-content">
              <div class="kpi-label">Productos Extraídos</div>
              <div class="kpi-value">{{ stats.total_products.toLocaleString() }}</div>
              <div class="kpi-subtitle">Total procesados</div>
            </div>
          </v-card-text>
        </v-card>
      </v-col>
      <v-col cols="12" sm="6" md="3">
        <v-card class="kpi-card elevation-1" :loading="loadingStats">
          <v-card-text>
            <div class="kpi-icon">
              <v-icon color="warning">mdi-alert-circle</v-icon>
            </div>
            <div class="kpi-content">
              <div class="kpi-label">Pendientes Validación</div>
              <div class="kpi-value">{{ stats.pending_validation }}</div>
              <div class="kpi-subtitle">Requieren revisión</div>
            </div>
          </v-card-text>
        </v-card>
      </v-col>
      <v-col cols="12" sm="6" md="3">
        <v-card class="kpi-card elevation-1" :loading="loadingStats">
          <v-card-text>
            <div class="kpi-icon">
              <v-icon color="info">mdi-clock-outline</v-icon>
            </div>
            <div class="kpi-content">
              <div class="kpi-label">Tiempo Promedio</div>
              <div class="kpi-value">{{ stats.avg_processing_time }}s</div>
              <div class="kpi-subtitle">Por documento</div>
            </div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- Upload Section -->
    <v-card v-if="!processingResults.length" class="main-card elevation-0 mb-6" outlined>
      <v-card-title class="pa-0">
        <v-toolbar flat class="minimal-toolbar">
          <h2 class="section-title">Procesamiento de Documentos con BOS AI</h2>
          <v-spacer></v-spacer>
          <v-chip small color="primary" text-color="white">
            <v-icon left small>mdi-robot</v-icon>
            BOS AI Activo
          </v-chip>
        </v-toolbar>
      </v-card-title>

      <v-card-text class="pa-6">
        <!-- Supplier and Category Selection -->
        <v-row class="mb-4">
          <v-col cols="12" md="6">
            <v-autocomplete
              v-model="selectedSupplier"
              :items="suppliers"
              label="Proveedor *"
              item-text="PartyName"
              outlined
              return-object
              :rules="f_required"
              prepend-inner-icon="mdi-account-tie"
              :loading="loading_suppliers"
            >
              <template v-slot:item="{ item }">
                <v-list-item-content>
                  <v-list-item-title>{{ item.PartyName }}</v-list-item-title>
                  <v-list-item-subtitle>{{
                    item.PartyIdentification
                  }}</v-list-item-subtitle>
                </v-list-item-content>
              </template>
            </v-autocomplete>
          </v-col>
          <v-col cols="12" md="6">
            <v-select
              v-model="documentType"
              :items="documentTypes"
              label="Tipo de Documento"
              outlined
              prepend-inner-icon="mdi-file-document"
            />
          </v-col>
        </v-row>

        <!-- Document Type Info -->
        <v-row class="mb-4">
          <v-col cols="12">
            <v-alert type="info" outlined dense class="mb-0">
              <v-icon left>mdi-information</v-icon>
              <strong>Tipos de productos compatibles:</strong> Cosméticos, Hogar,
              Limpieza, Electrónicos, Papelería, Juguetes, Alimentos, etc. El AI puede
              extraer hasta 25+ productos por documento.
            </v-alert>
          </v-col>
        </v-row>

        <!-- Upload Area -->
        <div
          class="upload-area"
          :class="{
            'drag-over': dragOver,
            'has-file': selectedFile,
            disabled: processing,
          }"
          @drop="handleDrop"
          @dragover.prevent="dragOver = true"
          @dragleave="dragOver = false"
          @click="!selectedFile && !processing && $refs.fileInput.click()"
        >
          <input
            ref="fileInput"
            type="file"
            accept="image/*,.xml,.zip,.xlsx,.xls,.csv,application/xml,text/xml,application/zip,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel,text/csv"
            @change="handleFileSelect"
            hidden
            :disabled="processing"
          />

          <div v-if="!selectedFile" class="upload-content">
            <v-icon size="64" color="primary">mdi-cloud-upload</v-icon>
            <h3 class="mt-4 mb-2">Arrastra tu documento aquí</h3>
            <p class="text-body-2 grey--text">o haz clic para seleccionar</p>
            <p class="text-caption grey--text">Formatos: JPG, PNG, JPEG (máx. 10MB), XML o ZIP de Factura Electrónica, o Excel/CSV (máx. 20MB)</p>
          </div>

          <div v-else class="file-preview">
            <!-- Preview para imágenes -->
            <div v-if="!isInvoiceFile && !isTabularFile" class="preview-image-container">
              <img :src="imagePreview" alt="Preview" class="preview-image" />
              <v-overlay v-if="processing" absolute>
                <v-progress-circular indeterminate color="white" size="40" />
              </v-overlay>
            </div>
            <!-- Preview para XML/ZIP -->
            <div v-else-if="isInvoiceFile" class="preview-xml-container">
              <v-icon size="80" :color="isZipFile ? 'orange' : 'success'">
                {{ isZipFile ? 'mdi-folder-zip' : 'mdi-file-xml-box' }}
              </v-icon>
              <v-overlay v-if="processing" absolute>
                <v-progress-circular indeterminate color="white" size="40" />
              </v-overlay>
            </div>
            <!-- Preview para Excel/CSV -->
            <div v-else class="preview-xml-container">
              <v-icon size="80" :color="isCsvFile ? 'teal' : 'green darken-2'">
                {{ isCsvFile ? 'mdi-file-delimited' : 'mdi-file-excel' }}
              </v-icon>
              <v-overlay v-if="processing" absolute>
                <v-progress-circular indeterminate color="white" size="40" />
              </v-overlay>
            </div>
            <div class="file-info">
              <h4>{{ selectedFile.name }}</h4>
              <p class="text-caption">{{ formatFileSize(selectedFile.size) }}</p>
              <v-chip v-if="isZipFile" small color="orange" dark class="mb-2">
                <v-icon left small>mdi-folder-zip</v-icon>
                Factura Electrónica ZIP
              </v-chip>
              <v-chip v-else-if="isXmlFile" small color="success" class="mb-2">
                <v-icon left small>mdi-file-xml-box</v-icon>
                Factura Electrónica XML
              </v-chip>
              <v-chip v-else-if="isExcelFile" small color="green darken-2" dark class="mb-2">
                <v-icon left small>mdi-file-excel</v-icon>
                Excel
              </v-chip>
              <v-chip v-else-if="isCsvFile" small color="teal" dark class="mb-2">
                <v-icon left small>mdi-file-delimited</v-icon>
                CSV
              </v-chip>
              <div class="mt-3">
                <!-- Botón para imágenes -->
                <v-btn
                  v-if="!isInvoiceFile && !isTabularFile"
                  color="primary"
                  @click="processDocument"
                  :loading="processing"
                  :disabled="!selectedSupplier"
                  large
                >
                  <v-icon left>mdi-robot</v-icon>
                  Procesar con Claude AI
                </v-btn>
                <!-- Botón para XML/ZIP -->
                <v-btn
                  v-else-if="isInvoiceFile"
                  color="success"
                  @click="processInvoiceFile"
                  :loading="processing"
                  large
                >
                  <v-icon left>{{ isZipFile ? 'mdi-folder-zip' : 'mdi-file-xml-box' }}</v-icon>
                  Procesar Factura
                </v-btn>
                <!-- Botón para Excel/CSV -->
                <v-btn
                  v-else
                  color="green darken-2"
                  dark
                  @click="processTabularFile"
                  :loading="processing"
                  :disabled="!selectedSupplier"
                  large
                >
                  <v-icon left>mdi-table-arrow-down</v-icon>
                  Importar y mapear columnas
                </v-btn>
                <!-- Botón Debug -->
                <v-btn
                  v-if="isInvoiceFile"
                  color="warning"
                  outlined
                  @click="debugXmlDocument"
                  class="ml-2"
                  :disabled="processing"
                >
                  <v-icon left>mdi-bug</v-icon>
                  Debug
                </v-btn>
                <v-btn text @click="clearFile" class="ml-2" :disabled="processing">
                  <v-icon left>mdi-close</v-icon>
                  Cancelar
                </v-btn>
              </div>
              <div v-if="!selectedSupplier && (!isInvoiceFile || isTabularFile)" class="mt-2">
                <v-chip small color="warning" text-color="white">
                  <v-icon left small>mdi-alert</v-icon>
                  Selecciona un proveedor primero
                </v-chip>
              </div>
              <div v-if="isInvoiceFile" class="mt-2">
                <v-chip small color="info" text-color="white">
                  <v-icon left small>mdi-information</v-icon>
                  El proveedor se extraerá automáticamente
                </v-chip>
              </div>
              <div v-if="isTabularFile" class="mt-2">
                <v-chip small color="info" text-color="white">
                  <v-icon left small>mdi-information</v-icon>
                  Cruzarás las columnas del archivo con los campos del producto
                </v-chip>
              </div>
            </div>
          </div>
        </div>

        <!-- Processing Status -->
        <v-card v-if="processing" class="mt-4" outlined>
          <v-card-text>
            <div class="text-center">
              <v-progress-circular indeterminate color="primary" size="40" class="mb-3" />
              <h4>{{ processingMessage }}</h4>
              <p class="text-body-2 grey--text">{{ processingDetail }}</p>

              <!-- Progress Steps -->
              <div class="mt-4">
                <v-stepper v-model="processingStep" flat>
                  <v-stepper-header>
                    <v-stepper-step
                      :complete="processingStep > 1"
                      step="1"
                      :color="processingStep >= 1 ? 'primary' : ''"
                    >
                      Preparando
                    </v-stepper-step>
                    <v-divider></v-divider>
                    <v-stepper-step
                      :complete="processingStep > 2"
                      step="2"
                      :color="processingStep >= 2 ? 'primary' : ''"
                    >
                      Enviando a AI
                    </v-stepper-step>
                    <v-divider></v-divider>
                    <v-stepper-step
                      :complete="processingStep > 3"
                      step="3"
                      :color="processingStep >= 3 ? 'primary' : ''"
                    >
                      Extrayendo
                    </v-stepper-step>
                    <v-divider></v-divider>
                    <v-stepper-step
                      :complete="processingStep > 4"
                      step="4"
                      :color="processingStep >= 4 ? 'success' : ''"
                    >
                      Completado
                    </v-stepper-step>
                  </v-stepper-header>
                </v-stepper>
              </div>
            </div>
          </v-card-text>
        </v-card>
      </v-card-text>
    </v-card>

    <!-- Results Section -->
    <v-card
      v-if="processingResults.length && !processing"
      class="main-card elevation-0"
      outlined
    >
      <v-card-title class="pa-0">
        <v-toolbar flat class="minimal-toolbar">
          <h2 class="section-title">Productos Extraídos por Claude AI</h2>
          <v-spacer></v-spacer>

          <div class="results-info mr-4">
            <v-chip small label color="primary">
              {{ processingResults.length }} productos
            </v-chip>
            <v-chip
              small
              label
              :color="pendingValidationCount > 0 ? 'warning' : 'success'"
              class="ml-2"
            >
              {{ pendingValidationCount }} requieren validación
            </v-chip>
            <v-chip small label color="info" class="ml-2">
              {{ formatCurrency(totalCost) }} total costo
            </v-chip>
          </div>

          <v-btn text @click="startNewProcess" class="ml-2">
            <v-icon left>mdi-plus</v-icon>
            Nuevo Documento
          </v-btn>
        </v-toolbar>
      </v-card-title>

      <!-- Quick Actions -->
      <v-card-text class="pb-0">
        <v-row>
          <v-col cols="12">
            <div class="d-flex align-center flex-wrap gap-2">
              <v-btn small outlined color="primary" @click="selectAllProducts">
                <v-icon left small>mdi-check-all</v-icon>
                Seleccionar Todo
              </v-btn>
              <v-btn small outlined color="warning" @click="flagExpensiveProducts">
                <v-icon left small>mdi-currency-usd</v-icon>
                Marcar Costosos
              </v-btn>
              <v-btn small outlined color="error" @click="reviewMissingData">
                <v-icon left small>mdi-alert</v-icon>
                Revisar Faltantes
              </v-btn>
              <v-btn small outlined color="success" @click="autoFixPrices">
                <v-icon left small>mdi-auto-fix</v-icon>
                Auto-Corregir Precios
              </v-btn>
            </div>
          </v-col>
        </v-row>
      </v-card-text>

      <!-- Read-Only Data Table -->
      <v-data-table
        :headers="productHeaders"
        :items="processingResults"
        :items-per-page="25"
        item-key="id"
        class="minimal-table clickable-rows"
        :footer-props="{
          'items-per-page-options': [10, 25, 50, 100],
        }"
        show-select
        v-model="selectedProducts"
        @click:row="editProduct"
      >
        <!-- Product Description - Read Only -->
        <template v-slot:item.descripcion="{ item }">
          <div class="product-cell">
            <span class="product-text">{{ item.descripcion }}</span>
            <v-chip
              v-if="!item.descripcion || item.descripcion.trim() === ''"
              x-small
              color="error"
              text-color="white"
            >
              Faltante
            </v-chip>
          </div>
        </template>

        <!-- Reference - Read Only -->
        <template v-slot:item.referencia="{ item }">
          <span class="product-text">{{ item.referencia || "N/A" }}</span>
        </template>

        <!-- Quantity - Read Only -->
        <template v-slot:item.cantidad="{ item }">
          <div class="text-center">
            <span class="product-text">{{ item.cantidad }}</span>
          </div>
        </template>

        <!-- Unit Cost - Read Only -->
        <template v-slot:item.costo_unitario="{ item }">
          <div class="text-right">
            <span class="product-text">{{ formatCurrency(item.costo_unitario) }}</span>
          </div>
        </template>

        <!-- Total - Read Only -->
        <template v-slot:item.total="{ item }">
          <div class="text-right">
            <strong :class="item.total > 100000 ? 'error--text' : ''">
              {{ formatCurrency(item.total) }}
            </strong>
          </div>
        </template>

        <!-- Category - Read Only -->
        <template v-slot:item.categoria="{ item }">
          <v-chip small :color="getCategoryColor(item.categoria)" text-color="white">
            {{ item.categoria }}
          </v-chip>
        </template>

        <!-- Group - Read Only -->
        <template v-slot:item.grupo="{ item }">
          <v-chip small outlined>
            {{ item.grupo }}
          </v-chip>
        </template>

        <!-- Suggested Price - Read Only -->
        <template v-slot:item.precio_venta_sugerido="{ item }">
          <div class="text-right">
            <div class="font-weight-bold">
              {{ formatCurrency(item.precio_venta_sugerido) }}
            </div>
            <div class="text-caption" :class="getMarginColor(item.margen_sugerido)">
              Margen: {{ item.margen_sugerido }}%
            </div>
          </div>
        </template>

        <!-- Alerts - Read Only -->
        <template v-slot:item.alertas="{ item }">
          <div v-if="item.alertas && item.alertas.length">
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-badge :content="item.alertas.length" color="warning" overlap>
                  <v-icon color="warning" v-bind="attrs" v-on="on">
                    mdi-alert-circle
                  </v-icon>
                </v-badge>
              </template>
              <div>
                <div v-for="alert in item.alertas" :key="alert" class="caption">
                  • {{ alert }}
                </div>
              </div>
            </v-tooltip>
          </div>
          <v-icon v-else color="success">mdi-check-circle</v-icon>
        </template>

        <!-- No data -->
        <template v-slot:no-data>
          <div class="text-center py-12">
            <v-icon size="64" color="grey lighten-2">mdi-file-document</v-icon>
            <h3 class="text-h6 mt-4 grey--text">No hay productos procesados</h3>
            <p class="grey--text">Sube un documento para comenzar</p>
          </div>
        </template>
      </v-data-table>

      <!-- Document Summary -->
      <v-card-text>
        <v-row>
          <v-col cols="12" md="6">
            <h4 class="mb-2">Información del Documento</h4>
            <div class="document-info">
              <v-simple-table dense>
                <tbody>
                  <tr>
                    <td><strong>Proveedor:</strong></td>
                    <td>{{ selectedSupplier ? selectedSupplier.PartyName : "N/A" }}</td>
                  </tr>
                  <tr>
                    <td><strong>Número:</strong></td>
                    <td>{{ documentInfo.proveedor_id || "N/A" }}</td>
                  </tr>
                  <tr>
                    <td><strong>Fecha:</strong></td>
                    <td>{{ documentInfo.processed_at || "N/A" }}</td>
                  </tr>
                  <tr>
                    <td><strong>Moneda:</strong></td>
                    <td>{{ documentInfo.currency || "COP" }}</td>
                  </tr>
                  <tr>
                    <td><strong>Tiempo Procesamiento:</strong></td>
                    <td>{{ documentInfo.processing_time_ms || 0 }}ms</td>
                  </tr>
                </tbody>
              </v-simple-table>
            </div>
          </v-col>
          <v-col cols="12" md="6">
            <h4 class="mb-2">Resumen Financiero</h4>
            <div class="financial-summary">
              <v-row>
                <v-col cols="6">
                  <div class="summary-item">
                    <div class="summary-label">Total Costo:</div>
                    <div class="summary-value text-h6">
                      {{ formatCurrency(totalCost) }}
                    </div>
                  </div>
                </v-col>
                <v-col cols="6">
                  <div class="summary-item">
                    <div class="summary-label">Venta Sugerida:</div>
                    <div class="summary-value text-h6 success--text">
                      {{ formatCurrency(totalSuggestedPrice) }}
                    </div>
                  </div>
                </v-col>
                <v-col cols="6">
                  <div class="summary-item">
                    <div class="summary-label">Productos:</div>
                    <div class="summary-value">{{ processingResults.length }}</div>
                  </div>
                </v-col>
                <v-col cols="6">
                  <div class="summary-item">
                    <div class="summary-label">Margen Promedio:</div>
                    <div class="summary-value" :class="getMarginColor(averageMargin)">
                      {{ averageMargin }}%
                    </div>
                  </div>
                </v-col>
                <v-col cols="6">
                  <div class="summary-item">
                    <div class="summary-label">Ganancia Potencial:</div>
                    <div class="summary-value success--text">
                      {{ formatCurrency(totalSuggestedPrice - totalCost) }}
                    </div>
                  </div>
                </v-col>
                <v-col cols="6">
                  <div class="summary-item">
                    <div class="summary-label">ROI:</div>
                    <div class="summary-value info--text">
                      {{
                        Math.round(((totalSuggestedPrice - totalCost) / totalCost) * 100)
                      }}%
                    </div>
                  </div>
                </v-col>
              </v-row>
            </div>
          </v-col>
        </v-row>
      </v-card-text>
      <v-card-actions>
        <v-row no-gutters style="min-height: 200px">
          <v-col
            cols="12"
            md="7"
            class="pa-4 d-flex flex-column"
            style="border-right: 1px solid #e0e0e0"
          >
            <div class="mb-4">
              <v-icon color="success" class="mr-2">mdi-tag-text</v-icon>
              <span class="text-uppercase font-weight-bold">Condiciones</span>
            </div>

            <div class="flex-grow-1">
              <v-select
                v-model="motive"
                :items="FP"
                item-value="codigo"
                item-text="valor"
                return-object
                outlined
                label="Seleccione el motivo"
                :rules="f_required"
                prepend-inner-icon="mdi-tag-text"
                hide-details
                dense
              />
            </div>
          </v-col>

          <v-col cols="12" md="5" class="pa-4 d-flex flex-column">
            <v-btn
              color="success"
              @click="executeFullProcess"
              :loading="savingProducts"
            >
              <v-icon left>mdi-package-variant-closed</v-icon>
              Crear productos en maestro
            </v-btn>
          </v-col>
        </v-row>
      </v-card-actions>
    </v-card>

    <!-- Product Edit Dialog - FORMULARIO COMPLETO -->
    <v-dialog v-model="editDialog" max-width="800px" persistent scrollable>
      <v-card v-if="editingProduct">
        <v-card-title class="pa-0">
          <v-toolbar flat color="primary" dark>
            <v-icon left>mdi-pencil</v-icon>
            <span class="text-h5">Editar Producto</span>
            <v-spacer></v-spacer>
            <v-btn icon @click="editDialog = false">
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </v-toolbar>
        </v-card-title>

        <v-card-text class="pa-6">
          <v-form ref="editForm" v-model="editFormValid" lazy-validation>
            <v-row>
              <!-- Descripción del Producto -->
              <v-col cols="12">
                <v-text-field
                  v-model="editingProduct.descripcion"
                  label="Descripción del Producto *"
                  outlined
                  :rules="[requiredRule]"
                  prepend-inner-icon="mdi-text"
                  counter="100"
                  maxlength="100"
                  hide-details="auto"
                />
              </v-col>

              <!-- Referencia y Cantidad -->
              <v-col cols="12" sm="6">
                <v-text-field
                  v-model="editingProduct.referencia"
                  label="Referencia/SKU"
                  outlined
                  prepend-inner-icon="mdi-barcode"
                  placeholder="REF-001"
                  hide-details="auto"
                />
              </v-col>
              <v-col cols="12" sm="6">
                <v-text-field
                  v-model.number="editingProduct.cantidad"
                  label="Cantidad *"
                  type="number"
                  outlined
                  :rules="[positiveNumberRule]"
                  prepend-inner-icon="mdi-counter"
                  min="1"
                  @input="updateEditTotals"
                  hide-details="auto"
                />
              </v-col>

              <!-- Costos y Precios -->
              <v-col cols="12" sm="6">
                <v-text-field
                  v-model.number="editingProduct.costo_unitario"
                  label="Costo Unitario *"
                  type="number"
                  step="0.01"
                  outlined
                  prefix="$"
                  :rules="[nonNegativeNumberRule]"
                  prepend-inner-icon="mdi-currency-usd"
                  min="0"
                  @input="updateEditTotals"
                  hide-details="auto"
                />
              </v-col>
              <v-col cols="12" sm="6">
                <v-text-field
                  :value="editingProduct.tax"
                  label="Impuesto"
                  outlined
                  prepend-inner-icon="mdi-calculator"
                  hide-details="auto"
                />
              </v-col>
              <v-col cols="12" sm="6">
                <v-text-field
                  :value="
                    formatCurrency(
                      editingProduct.cantidad * editingProduct.costo_unitario
                    )
                  "
                  label="Total Costo"
                  outlined
                  disabled
                  prepend-inner-icon="mdi-calculator"
                  hide-details="auto"
                />
              </v-col>

              <!-- Precio de Venta Sugerido -->
              <v-col cols="12" sm="6">
                <v-text-field
                  v-model.number="editingProduct.precio_venta_sugerido"
                  label="Precio Venta Sugerido"
                  type="number"
                  step="0.01"
                  outlined
                  prefix="$"
                  prepend-inner-icon="mdi-tag"
                  @input="calculateMargin"
                  hide-details="auto"
                />
              </v-col>
              <v-col cols="12" sm="6">
                <v-text-field
                  :value="editingProduct.margen_sugerido + '%'"
                  label="Margen Calculado"
                  outlined
                  disabled
                  hide-details="auto"
                  :prepend-inner-icon="
                    editingProduct.margen_sugerido >= 30
                      ? 'mdi-trending-up'
                      : 'mdi-trending-down'
                  "
                />
              </v-col>

              <!-- Clasificación -->
              <v-col cols="12" sm="6">
                <v-combobox
                  v-model="editingProduct.categoria"
                  :items="availableCategories"
                  label="Categoría *"
                  outlined
                  :rules="[requiredRule]"
                  prepend-inner-icon="mdi-shape"
                  hide-details="auto"
                  @input="updateProductCodes(editingProduct)"
                />
              </v-col>
              <v-col cols="12" sm="6">
                <v-select
                  v-model="editingProduct.grupo"
                  :items="availableGroups"
                  label="Grupo *"
                  outlined
                  :rules="[requiredRule]"
                  prepend-inner-icon="mdi-group"
                  hide-details="auto"
                />
              </v-col>

              <!-- Unidad -->
              <v-col cols="12" sm="6">
                <v-select
                  v-model="editingProduct.unidad"
                  :items="availableUnits"
                  label="Unidad de Medida"
                  outlined
                  prepend-inner-icon="mdi-ruler"
                  hide-details="auto"
                />
              </v-col>
              <v-col cols="12" md="6">
                <v-text-field
                  v-model="editingProduct.referencia"
                  label="Referencia/SKU *"
                  outlined
                  prepend-inner-icon="mdi-barcode"
                  @input="updateProductCodes(editingProduct)"
                  hide-details="auto"
                />
              </v-col>

              <!-- EAN editable: si se borra vuelve al SKU como fallback -->
              <v-col cols="12" md="6">
                <v-text-field
                  v-model="editingProduct.ean"
                  label="EAN / Código de barras"
                  outlined
                  prepend-inner-icon="mdi-barcode-scan"
                  placeholder="Vacío = usar SKU"
                  hide-details="auto"
                  @input="updateProductCodes(editingProduct)"
                />
              </v-col>

              <!-- Observaciones -->
              <v-col cols="12">
                <v-textarea
                  v-model="editingProduct.observaciones"
                  label="Observaciones"
                  outlined
                  rows="3"
                  prepend-inner-icon="mdi-note-text"
                  counter="250"
                  maxlength="250"
                  placeholder="Detalles adicionales como color, talla, características especiales..."
                  hide-details="auto"
                />
              </v-col>

              <!-- Mostrar códigos generados automáticamente -->
              <v-col cols="12">
                <v-alert type="info" dense outlined class="mb-3">
                  <div class="text-body-2">
                    <strong>Códigos generados automáticamente:</strong><br />
                    <strong>SKU:</strong>
                    {{ editingProduct.sku_sugerido || "Ingrese referencia" }}<br />
                    <strong>PLU:</strong>
                    {{ editingProduct.plu_sugerido || "Seleccione categoría" }}<br />
                    <strong>EAN final:</strong>
                    {{ editingProduct.ean_sugerido || "Igual al SKU" }}
                  </div>
                </v-alert>
              </v-col>

              <!-- Alertas del Producto -->
              <v-col
                cols="12"
                v-if="editingProduct.alertas && editingProduct.alertas.length"
              >
                <v-alert type="warning" outlined dense>
                  <div class="font-weight-bold mb-2">Alertas detectadas:</div>
                  <ul>
                    <li v-for="alert in editingProduct.alertas" :key="alert">
                      {{ alert }}
                    </li>
                  </ul>
                </v-alert>
              </v-col>
            </v-row>
          </v-form>
        </v-card-text>

        <v-divider></v-divider>

        <v-card-actions class="pa-4">
          <v-btn text @click="editDialog = false" :disabled="savingEdit">
            <v-icon left>mdi-cancel</v-icon>
            Cancelar
          </v-btn>
          <v-btn
            color="info"
            text
            @click="duplicateAndClose"
            :disabled="savingEdit"
          >
            <v-icon left>mdi-content-copy</v-icon>
            Duplicar
          </v-btn>
          <v-btn
            color="error"
            text
            @click="removeAndClose"
            :disabled="savingEdit"
          >
            <v-icon left>mdi-delete</v-icon>
            Eliminar
          </v-btn>
          <v-spacer></v-spacer>
          <v-btn
            color="success"
            @click="saveProductEdit"
            :loading="savingEdit"
            :disabled="!editFormValid"
          >
            <v-icon left>mdi-content-save</v-icon>
            Guardar
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Sheet Selector Dialog (solo para Excel multi-hoja) -->
    <v-dialog v-model="sheetSelectorDialog" max-width="500px" persistent>
      <v-card>
        <v-card-title class="pa-0">
          <v-toolbar flat color="green darken-2" dark>
            <v-icon left>mdi-file-excel</v-icon>
            <span>Seleccionar hoja</span>
          </v-toolbar>
        </v-card-title>
        <v-card-text class="pa-4">
          <p class="text-body-2 mb-3">
            El archivo contiene varias hojas. Elige cuál procesar:
          </p>
          <v-radio-group v-model="selectedSheetName" hide-details>
            <v-radio
              v-for="sheet in availableSheets"
              :key="sheet"
              :value="sheet"
            >
              <template v-slot:label>
                <div>
                  <strong>{{ sheet }}</strong>
                  <span class="text-caption grey--text ml-2">
                    ({{ sheetRowCounts[sheet] || 0 }} filas)
                  </span>
                </div>
              </template>
            </v-radio>
          </v-radio-group>
        </v-card-text>
        <v-divider></v-divider>
        <v-card-actions class="pa-4">
          <v-btn text @click="cancelTabularImport">
            <v-icon left>mdi-cancel</v-icon>
            Cancelar
          </v-btn>
          <v-spacer></v-spacer>
          <v-btn
            color="green darken-2"
            dark
            @click="confirmSheetSelection"
            :disabled="!selectedSheetName"
          >
            <v-icon left>mdi-arrow-right</v-icon>
            Continuar
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Column Mapping Dialog -->
    <v-dialog v-model="mappingDialog" max-width="1100px" persistent scrollable>
      <v-card>
        <v-card-title class="pa-0">
          <v-toolbar flat color="green darken-2" dark>
            <v-icon left>mdi-table-arrow-down</v-icon>
            <span>Cruzar columnas del archivo con campos del producto</span>
            <v-spacer></v-spacer>
            <v-chip
              v-if="loadedTemplateFromSupplier"
              small
              color="white"
              text-color="green darken-2"
            >
              <v-icon left small>mdi-content-save-check</v-icon>
              Plantilla cargada del proveedor
            </v-chip>
          </v-toolbar>
        </v-card-title>

        <v-card-text class="pa-4">
          <v-alert type="info" outlined dense class="mb-3">
            Asigna cada columna del archivo a un campo del producto. Los campos
            marcados con <strong>*</strong> son obligatorios.
            La sugerencia automática se basa en los nombres de columna.
          </v-alert>

          <v-row>
            <v-col cols="12" md="6">
              <h4 class="mb-2">Mapeo de columnas</h4>
              <v-simple-table dense class="mapping-table">
                <thead>
                  <tr>
                    <th>Columna archivo</th>
                    <th>Campo destino</th>
                    <th>Muestra</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(header, idx) in tabularHeaders" :key="idx">
                    <td>
                      <strong>{{ header }}</strong>
                    </td>
                    <td>
                      <v-select
                        v-model="columnMapping[header]"
                        :items="targetFields"
                        item-value="value"
                        item-text="text"
                        dense
                        hide-details
                        outlined
                      />
                    </td>
                    <td>
                      <span class="text-caption grey--text">
                        {{ sampleValueForHeader(header) }}
                      </span>
                    </td>
                  </tr>
                </tbody>
              </v-simple-table>
            </v-col>

            <v-col cols="12" md="6">
              <h4 class="mb-2">
                Preview ({{ Math.min(tabularRows.length, 5) }} de
                {{ tabularRows.length }} filas)
              </h4>
              <v-simple-table dense class="preview-table">
                <thead>
                  <tr>
                    <th>Descripción</th>
                    <th class="text-right">Cant.</th>
                    <th class="text-right">Costo unit.</th>
                    <th>Ref.</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(row, idx) in tabularPreviewRows" :key="idx">
                    <td>{{ row.descripcion || '—' }}</td>
                    <td class="text-right">{{ row.cantidad || '—' }}</td>
                    <td class="text-right">{{ row.costo_unitario || '—' }}</td>
                    <td>{{ row.referencia || '—' }}</td>
                  </tr>
                  <tr v-if="!tabularPreviewRows.length">
                    <td colspan="4" class="text-center grey--text">
                      Sin datos para previsualizar
                    </td>
                  </tr>
                </tbody>
              </v-simple-table>

              <v-checkbox
                v-model="saveTemplateChecked"
                label="Guardar este mapeo como plantilla para este proveedor"
                hide-details
                dense
                class="mt-4"
              />
            </v-col>
          </v-row>
        </v-card-text>

        <v-divider></v-divider>

        <v-card-actions class="pa-4">
          <v-btn text @click="cancelTabularImport">
            <v-icon left>mdi-cancel</v-icon>
            Cancelar
          </v-btn>
          <v-spacer></v-spacer>
          <v-btn
            color="green darken-2"
            dark
            @click="applyTabularMapping"
            :disabled="!mappingHasRequiredFields"
          >
            <v-icon left>mdi-check</v-icon>
            Aplicar mapeo
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Snackbar -->
    <v-snackbar
      v-model="snackbar.show"
      :color="snackbar.color"
      :timeout="snackbar.timeout"
      top
    >
      {{ snackbar.text }}
      <template v-slot:action="{ attrs }">
        <v-btn text v-bind="attrs" @click="snackbar.show = false">Cerrar</v-btn>
      </template>
    </v-snackbar>
    <docViewer
      v-if="orderDocument"
      :item="orderDocument"
      :dialog="showOrderDocument"
      @close="showOrderDocument = false"
    />

    <!-- Debug XML Dialog -->
    <v-dialog v-model="debugDialog" max-width="900px" scrollable>
      <v-card>
        <v-card-title class="pa-0">
          <v-toolbar flat color="warning" dark>
            <v-icon left>mdi-bug</v-icon>
            <span>Debug XML - Estructura del documento</span>
            <v-spacer></v-spacer>
            <v-btn icon @click="debugDialog = false">
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </v-toolbar>
        </v-card-title>
        <v-card-text class="pa-4" style="max-height: 600px;">
          <div v-if="debugInfo">
            <v-alert type="info" dense class="mb-3">
              <strong>Root Element:</strong> {{ debugInfo.rootElement }}<br>
              <strong>Invoice en texto:</strong> {{ debugInfo.invoicePosition }}<br>
              <strong>Invoice en Description:</strong> {{ debugInfo.descriptionWithInvoice }}
            </v-alert>

            <div v-if="debugInfo.invoiceSnippet" class="mb-4">
              <h4 class="mb-2">Invoice encontrado (snippet):</h4>
              <pre style="background: #e3f2fd; padding: 12px; border-radius: 4px; font-size: 11px; max-height: 200px; overflow: auto;">{{ debugInfo.invoiceSnippet }}</pre>
            </div>

            <h4 class="mb-2">Estructura del documento (primeros 3 niveles):</h4>
            <pre class="debug-tree" style="background: #f5f5f5; padding: 12px; border-radius: 4px; overflow-x: auto; font-size: 12px;">{{ debugInfo.structure }}</pre>

            <h4 class="mt-4 mb-2">InvoiceLine encontrados: {{ debugInfo.invoiceLineCount }}</h4>

            <div v-if="debugInfo.invoiceLines && debugInfo.invoiceLines.length">
              <v-expansion-panels>
                <v-expansion-panel v-for="(line, idx) in debugInfo.invoiceLines" :key="idx">
                  <v-expansion-panel-header>
                    Línea {{ idx + 1 }}: {{ line.description || 'Sin descripción' }}
                  </v-expansion-panel-header>
                  <v-expansion-panel-content>
                    <pre style="font-size: 11px; white-space: pre-wrap;">{{ line.raw }}</pre>
                  </v-expansion-panel-content>
                </v-expansion-panel>
              </v-expansion-panels>
            </div>

            <h4 class="mt-4 mb-2">Contenido XML (primeros 5000 caracteres):</h4>
            <pre class="debug-xml" style="background: #263238; color: #aed581; padding: 12px; border-radius: 4px; overflow-x: auto; font-size: 11px; max-height: 300px;">{{ debugInfo.rawXml }}</pre>
          </div>
        </v-card-text>
        <v-card-actions>
          <v-btn text @click="debugDialog = false">Cerrar</v-btn>
          <v-spacer></v-spacer>
          <v-btn color="primary" @click="copyDebugInfo">
            <v-icon left>mdi-content-copy</v-icon>
            Copiar Info
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import { webserver, getToday, formatCurrency } from "@/services/webserver.js";
import docViewer from "@/components/doc_viewer_docs.vue";
import JSZip from "jszip";
import * as XLSX from "xlsx";
import {
  resolveCodes,
  getCategoriaCode as getCategoriaCodeUtil,
} from "@/utils/skuPluGenerator.js";

// Diccionario de sinónimos para sugerir mapeo de columnas archivo → campo destino.
// Cada lista contiene variantes en español/inglés, abreviaciones, con y sin tildes.
const FIELD_SYNONYMS = {
  descripcion: [
    "descripcion","descripción","desc","nombre","producto","item","articulo","artículo",
    "description","name","detalle","concepto",
  ],
  referencia: [
    "ref","referencia","codigo","código","cod","sku","code","reference","cod prov","cod_proveedor",
  ],
  cantidad: [
    "cantidad","cant","qty","quantity","unidades","und","stock","cant.","cant ",
  ],
  costo_unitario: [
    "costo","costo unit","costo unitario","precio costo","unit cost","unitario",
    "precio unitario","price","cost","valor unit","vlr unit","vlr unitario",
    "p. unit","p.unit","precio",
  ],
  total: [
    "total","importe","subtotal","valor","amount","valor total","vlr total","total item",
  ],
  unidad: ["unidad","uom","medida","um","unit","u.m.","u m"],
  categoria: ["categoria","categoría","category","familia","linea","línea"],
  grupo: ["grupo","group","subcategoria","subcategoría","subgrupo"],
  ean: [
    "ean","barcode","codigo barras","código barras","gtin","cod barras","barras",
  ],
  observaciones: ["obs","observaciones","nota","notes","comentario","comentarios","detalle"],
  tax: ["iva","tax","impuesto","%iva","porc iva"],
  precio_venta: [
    "precio venta","precio_venta","precio pvp","pvp","venta","precio sugerido",
    "price","precio publico","precio público","sale price","pvp venta","precio v",
  ],
};

// Sugiere precio de venta: costo + 35% redondeado al siguiente mil.
// Ej. costo 5350 → 5350*1.35 = 7222.5 → ceil(7222.5/1000)*1000 = 8000.
function suggestSalePrice(costo) {
  var c = parseFloat(costo) || 0;
  if (c <= 0) return 0;
  return Math.ceil((c * 1.35) / 1000) * 1000;
}

// Campos destino disponibles en el selector del mapping (label visible + key interna).
const TARGET_FIELDS = [
  { value: "__ignore__", text: "(ignorar)" },
  { value: "descripcion", text: "Descripción *" },
  { value: "referencia", text: "Referencia" },
  { value: "cantidad", text: "Cantidad *" },
  { value: "costo_unitario", text: "Costo unitario *" },
  { value: "precio_venta", text: "Precio de venta" },
  { value: "total", text: "Total línea" },
  { value: "unidad", text: "Unidad" },
  { value: "categoria", text: "Categoría" },
  { value: "grupo", text: "Grupo" },
  { value: "ean", text: "EAN / Código barras" },
  { value: "observaciones", text: "Observaciones" },
  { value: "tax", text: "Impuesto (%)" },
];

export default {
  components: {
    docViewer,
  },
  data: function () {
    return {
      showOrderDocument: false,
      orderDocument: null,
      FP: [],
      master: [],
      motive: null,
      snackbar: {
        show: false,
        text: "",
        color: "info",
        timeout: 3000,
      },
      today: getToday(),

      // Stats con valores reales
      stats: {
        total_documents: 0,
        total_products: 0,
        pending_validation: 0,
        avg_processing_time: 0,
        total_value_processed: 0,
        documents_this_month: 0,
        products_this_month: 0,
      },
      loadingStats: false,

      // Suppliers
      suppliers: [],
      selectedSupplier: null,
      loading_suppliers: false,

      // Document processing
      documentTypes: [
        { text: "Factura", value: "invoice" },
        { text: "Recibo", value: "receipt" },
        { text: "Orden de Compra", value: "order" },
        { text: "Cotización", value: "quote" },
      ],
      documentType: "invoice",

      // File handling
      selectedFile: null,
      imagePreview: null,
      dragOver: false,

      // Processing
      processing: false,
      processingStep: 1,
      processingMessage: "Preparando imagen...",
      processingDetail: "",
      processingResults: [],
      documentInfo: {},

      // Product management
      selectedProducts: [],
      savingProducts: false,
      editDialog: false,
      editingProduct: null,
      editFormValid: false,
      savingEdit: false,

      // Debug XML
      debugDialog: false,
      debugInfo: null,

      // Categories and groups (loaded from settings)
      availableCategories: [],
      availableGroups: [],
      availableUnits: [
        "UND",
        "KG",
        "GR",
        "LT",
        "ML",
        "MT",
        "CM",
        "PAR",
        "SET",
        "CAJA",
        "PACK",
      ],

      // Validation rules
      f_required: [
        function (v) {
          return !!v || "Requerido";
        },
      ],

      // Headers for results table (read-only)
      productHeaders: [
        {
          text: "Descripción",
          value: "descripcion",
          width: "20%",
          sortable: false,
        },
        {
          text: "Referencia",
          value: "referencia",
          width: "12%",
          sortable: false,
        },
        {
          text: "Cant.",
          value: "cantidad",
          width: "8%",
          align: "center",
          sortable: false,
        },
        {
          text: "Costo Unit.",
          value: "costo_unitario",
          width: "10%",
          align: "right",
          sortable: false,
        },
        {
          text: "Total",
          value: "total",
          width: "10%",
          align: "right",
          sortable: false,
        },
        {
          text: "Categoría",
          value: "categoria",
          width: "10%",
          sortable: false,
        },
        {
          text: "Grupo",
          value: "grupo",
          width: "8%",
          sortable: false,
        },
        {
          text: "Precio Sugerido",
          value: "precio_venta_sugerido",
          width: "12%",
          align: "right",
          sortable: false,
        },
        {
          text: "Estado",
          value: "alertas",
          width: "8%",
          align: "center",
          sortable: false,
        },
      ],

      // ============ Importación tabular (Excel/CSV) ============
      // Workbook XLSX cargado en memoria; se conserva mientras está abierto el wizard.
      loadedWorkbook: null,
      // Nombres de hojas disponibles (solo si el archivo tiene >1).
      availableSheets: [],
      // Hoja activa seleccionada por el usuario.
      selectedSheetName: null,
      // Conteo de filas por hoja para mostrar en el selector.
      sheetRowCounts: {},
      // Diálogos del wizard.
      sheetSelectorDialog: false,
      mappingDialog: false,
      // Headers detectados (primera fila no vacía del sheet).
      tabularHeaders: [],
      // Filas crudas (arrays paralelos a tabularHeaders).
      tabularRows: [],
      // Mapeo header_normalizado → campo destino. Valores `__ignore__` se descartan.
      columnMapping: {},
      // Lista de targets para el v-select del dialog.
      targetFields: TARGET_FIELDS,
      // Si el mapeo viene de plantilla persistida del proveedor.
      loadedTemplateFromSupplier: false,
      // Toggle del checkbox "Guardar como plantilla del proveedor".
      saveTemplateChecked: true,
    };
  },

  computed: {
    pendingValidationCount: function () {
      return this.processingResults.filter(function (item) {
        return item.requiere_validacion || (item.alertas && item.alertas.length > 0);
      }).length;
    },

    canSaveProducts: function () {
      return (
        this.processingResults.length > 0 &&
        this.processingResults.every(function (item) {
          return item.descripcion && item.descripcion.trim().length > 0;
        })
      );
    },

    totalCost: function () {
      return this.processingResults.reduce(function (sum, item) {
        return sum + item.cantidad * item.costo_unitario;
      }, 0);
    },

    totalSuggestedPrice: function () {
      return this.processingResults.reduce(function (sum, item) {
        return sum + item.cantidad * item.precio_venta_sugerido;
      }, 0);
    },

    averageMargin: function () {
      if (this.processingResults.length === 0) return 0;
      var totalMargin = this.processingResults.reduce(function (sum, item) {
        return sum + (item.margen_sugerido || 0);
      }, 0);
      return Math.round(totalMargin / this.processingResults.length);
    },

    // Validation rules as computed properties
    requiredRule: function () {
      return function (v) {
        return !!v || "Requerido";
      };
    },

    positiveNumberRule: function () {
      return function (v) {
        return v > 0 || "Debe ser mayor a 0";
      };
    },

    nonNegativeNumberRule: function () {
      return function (v) {
        return v >= 0 || "Debe ser mayor o igual a 0";
      };
    },

    // Detectar si el archivo seleccionado es XML
    isXmlFile: function () {
      if (!this.selectedFile) return false;
      var fileName = this.selectedFile.name.toLowerCase();
      var fileType = this.selectedFile.type.toLowerCase();
      return (
        fileName.endsWith(".xml") ||
        fileType === "application/xml" ||
        fileType === "text/xml"
      );
    },

    // Detectar si el archivo seleccionado es ZIP
    isZipFile: function () {
      if (!this.selectedFile) return false;
      var fileName = this.selectedFile.name.toLowerCase();
      var fileType = this.selectedFile.type.toLowerCase();
      return (
        fileName.endsWith(".zip") ||
        fileType === "application/zip" ||
        fileType === "application/x-zip-compressed"
      );
    },

    // Detectar si es XML o ZIP (factura electrónica)
    isInvoiceFile: function () {
      return this.isXmlFile || this.isZipFile;
    },

    // Detectar Excel (.xlsx, .xls)
    isExcelFile: function () {
      if (!this.selectedFile) return false;
      var name = this.selectedFile.name.toLowerCase();
      return name.endsWith(".xlsx") || name.endsWith(".xls");
    },

    // Detectar CSV
    isCsvFile: function () {
      if (!this.selectedFile) return false;
      var name = this.selectedFile.name.toLowerCase();
      return name.endsWith(".csv");
    },

    // Cualquier archivo tabular procesable con XLSX
    isTabularFile: function () {
      return this.isExcelFile || this.isCsvFile;
    },

    // Filas en formato AI-product para preview (primeras 5)
    tabularPreviewRows: function () {
      if (!this.tabularRows.length || !this.tabularHeaders.length) return [];
      var sample = this.tabularRows.slice(0, 5);
      return this.mapRowsToAIShape(sample);
    },

    // Validación: debe haber al menos descripcion, cantidad y costo_unitario mapeados.
    mappingHasRequiredFields: function () {
      var values = Object.values(this.columnMapping || {});
      return (
        values.indexOf("descripcion") !== -1 &&
        values.indexOf("cantidad") !== -1 &&
        values.indexOf("costo_unitario") !== -1
      );
    },
  },

  mounted: function () {
    this.loadSuppliers();
    this.loadCategoriesAndGroups();
    this.loadRealStats();
    this.FP = window.settings["OP"] || [];
    //console.log(this.FP);
  },

  methods: {
    // ============ DEBUG METHODS ============
    debugXmlDocument: function () {
      var self = this;
      if (!this.selectedFile) {
        this.showMessage("No hay archivo seleccionado", "warning");
        return;
      }

      // Si es ZIP, extraer primero el XML
      if (this.isZipFile) {
        this.debugZipFile();
        return;
      }

      var reader = new FileReader();
      reader.onload = function (e) {
        self.processXmlForDebug(e.target.result);
      };
      reader.readAsText(this.selectedFile);
    },

    // Debug archivo ZIP
    debugZipFile: function () {
      var self = this;
      var reader = new FileReader();

      reader.onload = function (e) {
        JSZip.loadAsync(e.target.result)
          .then(function (zip) {
            var xmlFile = null;
            var xmlFileName = "";
            var zipContents = [];

            // Listar contenido del ZIP
            zip.forEach(function (relativePath, file) {
              zipContents.push(relativePath + (file.dir ? " (carpeta)" : ""));
              if (relativePath.toLowerCase().endsWith(".xml") && !file.dir) {
                if (!xmlFile || relativePath.toLowerCase().startsWith("fv")) {
                  xmlFile = file;
                  xmlFileName = relativePath;
                }
              }
            });

            if (!xmlFile) {
              self.debugInfo = {
                rootElement: "ZIP (sin XML encontrado)",
                invoicePosition: "N/A",
                descriptionWithInvoice: "N/A",
                structure: "Contenido del ZIP:\n" + zipContents.join("\n"),
                invoiceLineCount: 0,
                invoiceLines: [],
                rawXml: "No se encontró ningún archivo XML en el ZIP",
              };
              self.debugDialog = true;
              return;
            }

            return xmlFile.async("string").then(function (xmlContent) {
              self.processXmlForDebug(xmlContent, "ZIP: " + xmlFileName);
            });
          })
          .catch(function (error) {
            self.showMessage("Error leyendo ZIP: " + error.message, "error");
          });
      };

      reader.readAsArrayBuffer(this.selectedFile);
    },

    // Procesar XML para debug (usado por ambos: XML directo y ZIP)
    processXmlForDebug: function (xmlContent, sourceInfo) {
      var self = this;
      try {
        var parser = new DOMParser();
        var xmlDoc = parser.parseFromString(xmlContent, "text/xml");
        var rootElement = xmlDoc.documentElement;
        var rootName = rootElement.localName || rootElement.tagName;
        if (sourceInfo) rootName = sourceInfo + " → " + rootName;

        var namespaces = [];
        for (var i = 0; i < rootElement.attributes.length; i++) {
          var attr = rootElement.attributes[i];
          if (attr.name.startsWith("xmlns")) {
            namespaces.push(attr.name + "=" + attr.value.substring(0, 50) + "...");
          }
        }
        var structure = self.generateXmlTreeSimple(rootElement, 0, 3);
        var invoiceLines = [];
        var allElements = xmlDoc.getElementsByTagName("*");
        for (var j = 0; j < allElements.length; j++) {
          var el = allElements[j];
          if (el.localName === "InvoiceLine" || (el.tagName && el.tagName.indexOf("InvoiceLine") !== -1)) {
            var desc = "";
            var descEl = el.querySelector("Description") || el.getElementsByTagName("Description")[0];
            if (descEl) desc = descEl.textContent.substring(0, 100);
            invoiceLines.push({
              description: desc,
              raw: new XMLSerializer().serializeToString(el).substring(0, 2000),
            });
          }
        }
        // Buscar dónde está el Invoice embebido
        var invoicePosition = xmlContent.indexOf("<Invoice");
        var invoiceInfo = "No encontrado";
        var invoiceSnippet = "";
        if (invoicePosition !== -1) {
          invoiceInfo = "Encontrado en posición: " + invoicePosition;
          invoiceSnippet = xmlContent.substring(invoicePosition, invoicePosition + 1000);
        }

        // Buscar en elementos Description (donde suele estar el Invoice embebido)
        var allDescElements = xmlDoc.getElementsByTagName("*");
        var descriptionWithInvoice = null;
        for (var d = 0; d < allDescElements.length; d++) {
          var descElem = allDescElements[d];
          if (descElem.localName === "Description" && descElem.textContent.indexOf("<Invoice") !== -1) {
            descriptionWithInvoice = "Encontrado en Description, longitud: " + descElem.textContent.length;
            break;
          }
        }

        self.debugInfo = {
          rootElement: rootName,
          namespaces: namespaces.join("\n"),
          structure: structure,
          invoiceLineCount: invoiceLines.length,
          invoiceLines: invoiceLines.slice(0, 5),
          rawXml: xmlContent.substring(0, 3000),
          invoicePosition: invoiceInfo,
          invoiceSnippet: invoiceSnippet,
          descriptionWithInvoice: descriptionWithInvoice || "No encontrado en Description",
        };
        self.debugDialog = true;
      } catch (error) {
        self.showMessage("Error parseando XML: " + error.message, "error");
      }
    },

    generateXmlTreeSimple: function (element, level, maxLevel) {
      if (level >= maxLevel) return "";
      var indent = "  ".repeat(level);
      var result = indent + "<" + (element.localName || element.tagName) + ">\n";
      var children = element.children;
      for (var j = 0; j < children.length && j < 10; j++) {
        result += this.generateXmlTreeSimple(children[j], level + 1, maxLevel);
      }
      if (children.length > 10) {
        result += indent + "  ... (" + (children.length - 10) + " más)\n";
      }
      return result;
    },

    copyDebugInfo: function () {
      if (!this.debugInfo) return;
      var text = "=== DEBUG XML ===\n";
      text += "Root: " + this.debugInfo.rootElement + "\n";
      text += "InvoiceLines: " + this.debugInfo.invoiceLineCount + "\n\n";
      text += "=== ESTRUCTURA ===\n" + this.debugInfo.structure + "\n\n";
      text += "=== XML (primeros 5000 chars) ===\n" + this.debugInfo.rawXml;
      navigator.clipboard.writeText(text).then(function() {
        this.showMessage("Info copiada al clipboard", "success");
      }.bind(this));
    },
    // ============ END DEBUG METHODS ============

    createMockData() {
      //console.log("====>", this.selectedSupplier);
      if (!this.selectedSupplier) {
        this.showMessage("Selecciona un proveedor primero", "warning");
        return;
      }

      //console.log(this.selectedSupplier);

      // Simular datos extraídos por AI
      this.processingResults = [
        {
          id: 1,
          descripcion: "LABIAL MATE L'OREAL PARIS",
          referencia: "LOR001",
          cantidad: 12,
          costo_unitario: 25000,
          total: 300000,
          categoria: "SALUD Y BELLEZA",
          grupo: "PRODUCTOS PREMIUM",
          unidad: "UND",
          observaciones: "Color Rojo Intenso, Talla N/A",
          precio_venta_sugerido: 45000,
          margen_sugerido: 80,
          alertas: [],
          requiere_validacion: false,
        },
        {
          id: 2,
          descripcion: "SHAMPOO PANTENE 400ML",
          referencia: "PAN400",
          cantidad: 24,
          costo_unitario: 15000,
          total: 360000,
          categoria: "SALUD Y BELLEZA",
          grupo: "PRODUCTOS BÁSICOS",
          unidad: "UND",
          observaciones: "Para cabello graso, 400ML",
          precio_venta_sugerido: 28000,
          margen_sugerido: 87,
          alertas: [],
          requiere_validacion: false,
        },
        {
          id: 3,
          descripcion: "CREMA FACIAL NIVEA",
          referencia: "NIV250",
          cantidad: 6,
          costo_unitario: 18000,
          total: 108000,
          categoria: "SALUD Y BELLEZA",
          grupo: "PRODUCTOS BÁSICOS",
          unidad: "UND",
          observaciones: "Piel seca, 250ML",
          precio_venta_sugerido: 32000,
          margen_sugerido: 78,
          alertas: ["Producto de alto valor"],
          requiere_validacion: true,
        },
      ];

      // Simular documentInfo
      this.documentInfo = {
        document_number: "FAC-001234",
        date: "2024-03-15",
        currency: "COP",
        total_amount: "768000",
        processing_time_ms: 3500,
      };

      // Generar códigos para cada producto
      this.processingResults.forEach((product) => {
        this.updateProductCodes(product);
      });

      this.showMessage(
        "Datos mock creados: " + this.processingResults.length + " productos",
        "info"
      );
    },
    // Utility methods
    showMessage: function (text, color, timeout) {
      color = color || "info";
      timeout = timeout || 3000;
      this.snackbar = { show: true, text: text, color: color, timeout: timeout };
    },

    formatCurrency: formatCurrency,

    getMarginColor: function (margin) {
      if (margin >= 50) return "success--text";
      if (margin >= 30) return "warning--text";
      return "error--text";
    },

    getCategoryColor: function (category) {
      var colors = {
        COSMETICOS: "pink",
        HOGAR: "blue",
        LIMPIEZA: "teal",
        ELECTRONICOS: "purple",
        PAPELERIA: "orange",
        JUGUETES: "green",
        ALIMENTOS: "brown",
      };
      return colors[category] || "grey";
    },

    // Cargar estadísticas reales usando SOLO endpoints existentes
    loadRealStats: function () {
      var self = this;
      self.loadingStats = true;

      // Usar el endpoint que ya existe: get_dataTable para obtener estadísticas
      var statsQuery = {
        store: window.profile.store_id,
        table: "docs", // Tabla de documentos para contar procesados
        filters: [
          {
            field: "store",
            operator: "=",
            value: window.profile.store_id.substring(0, 6),
          },
          {
            field: "doc_type",
            operator: "=",
            value: "AI-IMPORT", // Tipo de documento para imports de AI
          },
        ],
      };

      webserver(
        "get_dataTable",
        statsQuery,
        function (data) {
          self.loadingStats = false;

          // Calcular estadísticas basadas en los datos reales
          var totalDocs = data.length;
          var totalProducts = 0;
          //var pendingValidation = 0;

          // Sumar productos de todos los documentos AI
          data.forEach(function (doc) {
            // Aproximar productos por documento (ajustar según tu lógica)
            totalProducts += parseInt(doc.notes) || 0; // Si guardas count en notes
          });

          self.stats = {
            total_documents: totalDocs,
            total_products: totalProducts,
            pending_validation: Math.floor(totalProducts * 0.1), // 10% aproximado
            avg_processing_time: (4.2 + Math.random() * 2).toFixed(1), // Simulated
            total_value_processed: 0,
            documents_this_month: totalDocs, // Simplificado
            products_this_month: totalProducts,
          };

          //console.log("📊 Estadísticas calculadas:", self.stats);
        },
        function (error) {
          self.loadingStats = false;
          console.warn("⚠️ Error cargando estadísticas:", error);

          // Mantener stats en 0 si no se pueden cargar
          self.stats = {
            total_documents: 0,
            total_products: 0,
            pending_validation: 0,
            avg_processing_time: 0,
            total_value_processed: 0,
            documents_this_month: 0,
            products_this_month: 0,
          };
        }
      );
    },

    // Load suppliers
    loadSuppliers: function () {
      var self = this;
      self.loading_suppliers = true;

      var qry = {
        store: window.profile.store_id,
        table: "proveedores",
        filters: [
          {
            field: "store",
            operator: "=",
            value: window.profile.store_id.substring(0, 6),
          },
        ],
      };

      webserver(
        "get_dataTable",
        qry,
        function (data) {
          self.loading_suppliers = false;
          self.suppliers = data.map(function (itm) {
            return {
              codigo: itm.codigo,
              PartyName: itm.nombre,
              PartyIdentification: itm.id_nit,
              AddressLine: itm.direccion,
              CityName: itm.ciudad,
              PartyEmail: itm.email,
            };
          });
          self.showMessage(self.suppliers.length + " proveedores cargados", "success");
        },
        function (error) {
          self.loading_suppliers = false;
          self.showMessage("Error al cargar proveedores: " + error, "error");
        }
      );
    },

    loadCategoriesAndGroups: function () {
      var settings = window.settings || {};

      // ✅ CORRECTO: Mapear para extraer solo los valores
      this.availableCategories = settings.CATEGORIA
        ? settings.CATEGORIA.map(function (cat) {
            return cat.valor;
          })
        : [
            "ALIMENTACIÓN Y BEBIDAS",
            "TEXTIL Y CONFECCIÓN",
            "CALZADO Y COMPLEMENTOS",
            "HOGAR Y DECORACIÓN",
            "ELECTRÓNICA Y TECNOLOGÍA",
            "LIBROS Y PAPELERÍA",
            "DEPORTES Y OCIO",
            "SALUD Y BELLEZA",
            "AUTOMÓVIL",
            "SERVICIOS",
            "OTROS",
          ];

      this.availableGroups = settings.GRUPO
        ? settings.GRUPO.map(function (group) {
            return group.valor;
          })
        : [
            "PRODUCTOS BÁSICOS",
            "PRODUCTOS PREMIUM",
            "OFERTAS Y PROMOCIONES",
            "TEMPORADA",
            "DESCATALOGADOS",
          ];
    },

    // File handling
    handleDrop: function (e) {
      e.preventDefault();
      this.dragOver = false;
      if (this.processing) return;

      var files = e.dataTransfer.files;
      if (files.length > 0) {
        this.handleFile(files[0]);
      }
    },

    handleFileSelect: function (e) {
      var file = e.target.files[0];
      if (file && !this.processing) {
        this.handleFile(file);
      }
    },

    handleFile: function (file) {
      if (!this.validateFile(file)) return;
      this.selectedFile = file;

      // Solo crear preview para imágenes; XML/ZIP/Excel/CSV usan icono.
      var fileName = file.name.toLowerCase();
      var isNonImage =
        fileName.endsWith(".xml") ||
        fileName.endsWith(".zip") ||
        fileName.endsWith(".xlsx") ||
        fileName.endsWith(".xls") ||
        fileName.endsWith(".csv");
      if (!isNonImage) {
        this.createImagePreview(file);
      }
    },

    validateFile: function (file) {
      var allowedImageTypes = ["image/jpeg", "image/jpg", "image/png"];
      var allowedXmlTypes = ["application/xml", "text/xml"];
      var allowedZipTypes = ["application/zip", "application/x-zip-compressed"];
      var fileName = file.name.toLowerCase();

      // Verificar si es XML por extensión o tipo
      var isXml =
        fileName.endsWith(".xml") || allowedXmlTypes.indexOf(file.type) !== -1;
      var isZip =
        fileName.endsWith(".zip") || allowedZipTypes.indexOf(file.type) !== -1;
      var isImage = allowedImageTypes.indexOf(file.type) !== -1;
      var isExcel = fileName.endsWith(".xlsx") || fileName.endsWith(".xls");
      var isCsv = fileName.endsWith(".csv");
      var isTabular = isExcel || isCsv;

      if (!isXml && !isZip && !isImage && !isTabular) {
        this.showMessage(
          "Tipo de archivo no permitido. Solo JPG, PNG, JPEG, XML, ZIP, Excel o CSV.",
          "error"
        );
        return false;
      }

      // Imágenes/XML/ZIP: 10 MB. Excel/CSV: 20 MB (suelen ser más voluminosos).
      var maxSize = isTabular ? 20 * 1024 * 1024 : 10 * 1024 * 1024;
      if (file.size > maxSize) {
        var label = isTabular ? "20MB" : "10MB";
        this.showMessage("Archivo demasiado grande. Máximo " + label + ".", "error");
        return false;
      }

      return true;
    },

    createImagePreview: function (file) {
      var self = this;
      var reader = new FileReader();
      reader.onload = function (e) {
        self.imagePreview = e.target.result;
      };
      reader.readAsDataURL(file);
    },

    clearFile: function () {
      this.selectedFile = null;
      this.imagePreview = null;
      this.$refs.fileInput.value = "";
    },

    formatFileSize: function (bytes) {
      if (bytes === 0) return "0 Bytes";
      var k = 1024;
      var sizes = ["Bytes", "KB", "MB"];
      var i = Math.floor(Math.log(bytes) / Math.log(k));
      return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
    },

    // Document processing - SOLO USA ENDPOINTS EXISTENTES
    processDocument: function () {
      var self = this;
      if (!this.selectedSupplier) {
        this.showMessage("Selecciona un proveedor", "warning");
        return;
      }

      if (!this.selectedFile) {
        this.showMessage("Selecciona un documento", "warning");
        return;
      }

      this.processing = true;
      this.processingStep = 1;
      this.processingMessage = "Optimizando imagen...";
      this.processingDetail = "Preparando imagen para el análisis de Claude AI";

      this.convertToBase64(this.selectedFile)
        .then(function (base64Image) {
          self.processingStep = 2;
          self.processingMessage = "Enviando a Claude AI...";
          self.processingDetail = "Analizando documento con inteligencia artificial";

          var requestData = {
            image: base64Image.split(",")[1],
            proveedor_id: self.selectedSupplier.codigo,
            proveedor: self.selectedSupplier.PartyName,
            categorias: self.availableCategories,
            grupos: self.availableGroups,
            filename: self.selectedFile.name,
          };

          self.processingStep = 3;
          self.processingMessage = "Extrayendo productos...";
          self.processingDetail =
            "Claude AI está analizando y extrayendo productos del documento";

          console.log(requestData);

          //USAR SOLO EL ENDPOINT QUE EXISTE
          webserver(
            "process_document_image",
            requestData,
            function (response) {
              self.processingStep = 4;
              self.processing = false;

              if (response.success) {
                self.processingResults = response.data;
                self.documentInfo = response.metadata;
                console.log(response.metadata);

                // Recalcular precio sugerido con la formula establecida
                // (costo + 35% redondeado al siguiente mil) y regenerar codigos.
                // El AI puede devolver precios sugeridos que no respetan la formula.
                self.processingResults.forEach(function (product) {
                  var costo = parseFloat(product.costo_unitario) || 0;
                  product.precio_venta_sugerido = suggestSalePrice(costo);
                  product.margen_sugerido = costo > 0
                    ? Math.round(((product.precio_venta_sugerido - costo) / costo) * 100)
                    : 35;
                  self.updateProductCodes(product);
                });

                self.showMessage(
                  response.data.length +
                    " productos extraídos exitosamente por Claude AI",
                  "success",
                  5000
                );

                self.updateStatsAfterProcessing(response.data.length, response.metadata);
              } else {
                throw new Error(response.error || "Error desconocido");
              }
            },
            function (error) {
              self.processing = false;
              self.showMessage("Error procesando documento: " + error, "error");
            }
          );
        })
        .catch(function (error) {
          self.processing = false;
          self.showMessage("Error: " + error.message, "error");
        });
    },

    // ========================================
    // PROCESAMIENTO DE XML DE FACTURA ELECTRÓNICA
    // ========================================

    // Método principal para procesar factura (ZIP o XML)
    processInvoiceFile: function () {
      if (this.isZipFile) {
        this.processZipFile();
      } else {
        this.processXmlDocument();
      }
    },

    // Procesar archivo ZIP de DIAN
    processZipFile: function () {
      var self = this;

      if (!this.selectedFile) {
        this.showMessage("Selecciona un archivo ZIP", "warning");
        return;
      }

      this.processing = true;
      this.processingStep = 1;
      this.processingMessage = "Leyendo archivo ZIP...";
      this.processingDetail = "Extrayendo factura electrónica";

      var reader = new FileReader();
      reader.onload = function (e) {
        JSZip.loadAsync(e.target.result)
          .then(function (zip) {
            self.processingStep = 2;
            self.processingMessage = "Buscando XML en el ZIP...";

            // Buscar el archivo XML dentro del ZIP
            var xmlFile = null;
            var xmlFileName = "";

            // Buscar archivos XML en el ZIP
            zip.forEach(function (relativePath, file) {
              if (relativePath.toLowerCase().endsWith(".xml") && !file.dir) {
                // Priorizar archivos que empiecen con 'fv' (factura de venta)
                if (!xmlFile || relativePath.toLowerCase().startsWith("fv")) {
                  xmlFile = file;
                  xmlFileName = relativePath;
                }
              }
            });

            if (!xmlFile) {
              throw new Error("No se encontró ningún archivo XML en el ZIP");
            }

            console.log("📄 XML encontrado en ZIP:", xmlFileName);
            self.processingDetail = "Procesando: " + xmlFileName;

            // Leer el contenido del XML
            return xmlFile.async("string");
          })
          .then(function (xmlContent) {
            self.processingStep = 3;
            self.processingMessage = "Parseando XML...";
            self.processingDetail = "Extrayendo datos de la factura";

            var parser = new DOMParser();
            var xmlDoc = parser.parseFromString(xmlContent, "text/xml");

            // Verificar errores de parseo
            var parseError = xmlDoc.querySelector("parsererror");
            if (parseError) {
              throw new Error("El archivo XML no es válido");
            }

            // Detectar si es un AttachedDocument y extraer el Invoice embebido
            xmlDoc = self.extractInvoiceFromAttachedDocument(xmlDoc, parser);

            // Extraer información del proveedor
            var supplierInfo = self.extractSupplierFromXml(xmlDoc);

            // Buscar o crear proveedor
            self.findOrCreateSupplier(supplierInfo, function (supplier) {
              self.selectedSupplier = supplier;

              // Extraer productos del XML
              var products = self.extractProductsFromXml(xmlDoc);

              if (products.length === 0) {
                throw new Error("No se encontraron productos en el XML");
              }

              // Extraer información del documento
              var docInfo = self.extractDocumentInfoFromXml(xmlDoc);

              self.processingStep = 4;
              self.processing = false;
              self.processingResults = products;
              self.documentInfo = docInfo;

              // Generar códigos para cada producto
              self.processingResults.forEach(function (product) {
                self.updateProductCodes(product);
              });

              self.showMessage(
                products.length + " productos extraídos del ZIP exitosamente",
                "success",
                5000
              );

              self.updateStatsAfterProcessing(products.length, docInfo);
            });
          })
          .catch(function (error) {
            self.processing = false;
            self.showMessage("Error procesando ZIP: " + error.message, "error");
            console.error("Error ZIP:", error);
          });
      };

      reader.onerror = function () {
        self.processing = false;
        self.showMessage("Error al leer el archivo ZIP", "error");
      };

      reader.readAsArrayBuffer(this.selectedFile);
    },

    // Extraer Invoice desde AttachedDocument si es necesario
    extractInvoiceFromAttachedDocument: function (xmlDoc, parser) {
      var rootName = xmlDoc.documentElement.localName || xmlDoc.documentElement.tagName;
      console.log("🔍 Root element:", rootName);

      // Si ya es un Invoice, retornar tal cual
      if (rootName === "Invoice" || rootName.indexOf("Invoice") !== -1) {
        console.log("✅ El documento ya es un Invoice directo");
        return xmlDoc;
      }

      // Si es un AttachedDocument, buscar el Invoice embebido
      if (rootName === "AttachedDocument" || rootName.indexOf("AttachedDocument") !== -1) {
        console.log("📎 Detectado AttachedDocument, buscando Invoice embebido...");

        // Buscar en Description (puede contener el Invoice como CDATA)
        var descriptions = xmlDoc.getElementsByTagName("*");
        for (var i = 0; i < descriptions.length; i++) {
          var elem = descriptions[i];
          if (elem.localName === "Description" || elem.tagName.indexOf("Description") !== -1) {
            var content = elem.textContent;
            // Verificar si contiene un Invoice XML
            if (content && content.indexOf("<Invoice") !== -1) {
              console.log("📄 Encontrado Invoice en Description, extrayendo...");
              // Extraer desde <Invoice hasta </Invoice>
              var invoiceStart = content.indexOf("<Invoice");
              var invoiceEnd = content.lastIndexOf("</Invoice>") + "</Invoice>".length;
              if (invoiceStart !== -1 && invoiceEnd > invoiceStart) {
                var invoiceXml = content.substring(invoiceStart, invoiceEnd);
                var invoiceDoc = parser.parseFromString(invoiceXml, "text/xml");
                var parseError = invoiceDoc.querySelector("parsererror");
                if (!parseError) {
                  console.log("✅ Invoice extraído exitosamente de Description");
                  return invoiceDoc;
                }
              }
            }
          }
        }

        // Buscar Invoice como elemento hijo directo o anidado
        var invoiceElements = xmlDoc.getElementsByTagName("*");
        for (var j = 0; j < invoiceElements.length; j++) {
          var el = invoiceElements[j];
          if (el.localName === "Invoice") {
            console.log("✅ Encontrado elemento Invoice directo");
            // Crear nuevo documento con solo el Invoice
            var serializer = new XMLSerializer();
            var invoiceStr = serializer.serializeToString(el);
            var newDoc = parser.parseFromString(invoiceStr, "text/xml");
            if (!newDoc.querySelector("parsererror")) {
              return newDoc;
            }
          }
        }

        // Buscar en Attachment > ExternalReference > Description
        var attachments = xmlDoc.querySelectorAll("Attachment, cac\\:Attachment");
        for (var k = 0; k < attachments.length; k++) {
          var attachment = attachments[k];
          var extRef = attachment.querySelector("ExternalReference, cac\\:ExternalReference");
          if (extRef) {
            var desc = extRef.querySelector("Description, cbc\\:Description");
            if (desc) {
              var descContent = desc.textContent;
              if (descContent && descContent.indexOf("<Invoice") !== -1) {
                console.log("📄 Encontrado Invoice en Attachment/ExternalReference/Description");
                var invStart = descContent.indexOf("<Invoice");
                var invEnd = descContent.lastIndexOf("</Invoice>") + "</Invoice>".length;
                if (invStart !== -1 && invEnd > invStart) {
                  var invXml = descContent.substring(invStart, invEnd);
                  // Decodificar entidades HTML si es necesario
                  invXml = invXml.replace(/&lt;/g, "<").replace(/&gt;/g, ">").replace(/&amp;/g, "&").replace(/&quot;/g, '"');
                  var invDoc = parser.parseFromString(invXml, "text/xml");
                  if (!invDoc.querySelector("parsererror")) {
                    console.log("✅ Invoice extraído de Attachment");
                    return invDoc;
                  }
                }
              }
            }
          }
        }

        console.log("⚠️ No se pudo extraer Invoice del AttachedDocument");
      }

      // Si no es ninguno de los anteriores, retornar el documento original
      console.log("ℹ️ Procesando documento como está");
      return xmlDoc;
    },

    processXmlDocument: function () {
      var self = this;

      if (!this.selectedFile) {
        this.showMessage("Selecciona un archivo XML", "warning");
        return;
      }

      this.processing = true;
      this.processingStep = 1;
      this.processingMessage = "Leyendo archivo XML...";
      this.processingDetail = "Cargando factura electrónica";

      // Leer el archivo XML
      var reader = new FileReader();
      reader.onload = function (e) {
        try {
          self.processingStep = 2;
          self.processingMessage = "Parseando XML...";
          self.processingDetail = "Extrayendo datos de la factura";

          var xmlContent = e.target.result;
          var parser = new DOMParser();
          var xmlDoc = parser.parseFromString(xmlContent, "text/xml");

          // Verificar errores de parseo
          var parseError = xmlDoc.querySelector("parsererror");
          if (parseError) {
            throw new Error("El archivo XML no es válido");
          }

          // Detectar si es un AttachedDocument y extraer el Invoice embebido
          xmlDoc = self.extractInvoiceFromAttachedDocument(xmlDoc, parser);

          self.processingStep = 3;
          self.processingMessage = "Extrayendo productos...";
          self.processingDetail = "Procesando líneas de factura";

          // Extraer información del proveedor
          var supplierInfo = self.extractSupplierFromXml(xmlDoc);

          // Buscar o crear proveedor
          self.findOrCreateSupplier(supplierInfo, function (supplier) {
            self.selectedSupplier = supplier;

            // Extraer productos del XML
            var products = self.extractProductsFromXml(xmlDoc);

            if (products.length === 0) {
              throw new Error("No se encontraron productos en el XML");
            }

            // Extraer información del documento
            var docInfo = self.extractDocumentInfoFromXml(xmlDoc);

            self.processingStep = 4;
            self.processing = false;
            self.processingResults = products;
            self.documentInfo = docInfo;

            // Generar códigos para cada producto
            self.processingResults.forEach(function (product) {
              self.updateProductCodes(product);
            });

            self.showMessage(
              products.length + " productos extraídos del XML exitosamente",
              "success",
              5000
            );

            self.updateStatsAfterProcessing(products.length, docInfo);
          });
        } catch (error) {
          self.processing = false;
          self.showMessage("Error procesando XML: " + error.message, "error");
        }
      };

      reader.onerror = function () {
        self.processing = false;
        self.showMessage("Error al leer el archivo XML", "error");
      };

      reader.readAsText(this.selectedFile);
    },

    // Extraer información del proveedor desde el XML
    extractSupplierFromXml: function (xmlDoc) {
      var supplier = {
        PartyName: "",
        PartyIdentification: "",
        AddressLine: "",
        CityName: "",
        PartyEmail: "",
      };

      try {
        // Namespaces usados en facturas electrónicas colombianas (UBL 2.1)
        var ns = {
          cbc: "urn:oasis:names:specification:ubl:schema:xsd:CommonBasicComponents-2",
          cac: "urn:oasis:names:specification:ubl:schema:xsd:CommonAggregateComponents-2",
        };

        // Buscar el nodo AccountingSupplierParty
        var supplierParty = xmlDoc.getElementsByTagNameNS(
          ns.cac,
          "AccountingSupplierParty"
        )[0];

        if (!supplierParty) {
          // Intentar sin namespace
          supplierParty = xmlDoc.querySelector(
            "AccountingSupplierParty, cac\\:AccountingSupplierParty"
          );
        }

        if (supplierParty) {
          // Nombre del proveedor
          var registrationName =
            supplierParty.querySelector(
              "RegistrationName, cbc\\:RegistrationName"
            ) ||
            supplierParty.getElementsByTagNameNS(ns.cbc, "RegistrationName")[0];
          if (registrationName) {
            supplier.PartyName = registrationName.textContent.trim();
          }

          // NIT/Identificación
          var companyID =
            supplierParty.querySelector("CompanyID, cbc\\:CompanyID") ||
            supplierParty.getElementsByTagNameNS(ns.cbc, "CompanyID")[0];
          if (companyID) {
            supplier.PartyIdentification = companyID.textContent.trim();
          }

          // Dirección
          var addressLine =
            supplierParty.querySelector("Line, cbc\\:Line") ||
            supplierParty.getElementsByTagNameNS(ns.cbc, "Line")[0];
          if (addressLine) {
            supplier.AddressLine = addressLine.textContent.trim();
          }

          // Ciudad
          var cityName =
            supplierParty.querySelector("CityName, cbc\\:CityName") ||
            supplierParty.getElementsByTagNameNS(ns.cbc, "CityName")[0];
          if (cityName) {
            supplier.CityName = cityName.textContent.trim();
          }

          // Email
          var email =
            supplierParty.querySelector(
              "ElectronicMail, cbc\\:ElectronicMail"
            ) ||
            supplierParty.getElementsByTagNameNS(ns.cbc, "ElectronicMail")[0];
          if (email) {
            supplier.PartyEmail = email.textContent.trim();
          }
        }
      } catch (error) {
        console.warn("Error extrayendo proveedor del XML:", error);
      }

      return supplier;
    },

    // Buscar proveedor existente o preparar datos para uno nuevo
    findOrCreateSupplier: function (supplierInfo, callback) {
      var self = this;

      // Buscar en la lista de proveedores cargados
      var existingSupplier = this.suppliers.find(function (s) {
        return s.PartyIdentification === supplierInfo.PartyIdentification;
      });

      if (existingSupplier) {
        callback(existingSupplier);
        return;
      }

      // Si no existe, crear un objeto temporal con los datos del XML
      var newSupplier = {
        codigo: "XML-" + Date.now(), // Código temporal
        PartyName: supplierInfo.PartyName || "Proveedor XML",
        PartyIdentification: supplierInfo.PartyIdentification || "N/A",
        AddressLine: supplierInfo.AddressLine || "",
        CityName: supplierInfo.CityName || "",
        PartyEmail: supplierInfo.PartyEmail || "",
        isFromXml: true, // Marcar que viene del XML
      };

      self.showMessage(
        "Proveedor del XML: " + newSupplier.PartyName,
        "info",
        3000
      );
      callback(newSupplier);
    },

    // Extraer productos desde las líneas de factura
    extractProductsFromXml: function (xmlDoc) {
      var self = this;
      var products = [];

      try {
        var ns = {
          cbc: "urn:oasis:names:specification:ubl:schema:xsd:CommonBasicComponents-2",
          cac: "urn:oasis:names:specification:ubl:schema:xsd:CommonAggregateComponents-2",
        };

        console.log("🔍 Buscando InvoiceLine en el XML...");

        // Buscar InvoiceLine con namespace
        var invoiceLines = xmlDoc.getElementsByTagNameNS(ns.cac, "InvoiceLine");
        console.log("📦 InvoiceLine con namespace cac:", invoiceLines.length);

        if (invoiceLines.length === 0) {
          // Intentar sin namespace específico
          invoiceLines = xmlDoc.querySelectorAll("InvoiceLine");
          console.log("📦 InvoiceLine sin namespace:", invoiceLines.length);
        }

        if (invoiceLines.length === 0) {
          // Intentar con prefijo cac:
          invoiceLines = xmlDoc.querySelectorAll("cac\\:InvoiceLine");
          console.log("📦 InvoiceLine con cac\\::", invoiceLines.length);
        }

        if (invoiceLines.length === 0) {
          // Intentar buscar todos los elementos que contengan "InvoiceLine"
          var allElements = xmlDoc.getElementsByTagName("*");
          var foundLines = [];
          for (var j = 0; j < allElements.length; j++) {
            if (allElements[j].localName === "InvoiceLine") {
              foundLines.push(allElements[j]);
            }
          }
          if (foundLines.length > 0) {
            invoiceLines = foundLines;
            console.log("📦 InvoiceLine por localName:", invoiceLines.length);
          }
        }

        // Debug: mostrar estructura del documento
        if (invoiceLines.length === 0) {
          console.log("⚠️ No se encontraron InvoiceLine. Estructura del XML:");
          var rootElement = xmlDoc.documentElement;
          console.log("Root element:", rootElement.tagName);
          console.log("Root localName:", rootElement.localName);

          // Listar primeros niveles
          var children = rootElement.children;
          for (var k = 0; k < Math.min(children.length, 10); k++) {
            console.log("  Child:", children[k].tagName || children[k].localName);
          }
        }

        var lineId = 1;
        for (var i = 0; i < invoiceLines.length; i++) {
          var line = invoiceLines[i];
          var product = self.parseInvoiceLine(line, ns, lineId);
          if (product) {
            products.push(product);
            lineId++;
          }
        }

        console.log("✅ Productos extraídos:", products.length);
      } catch (error) {
        console.error("❌ Error extrayendo productos del XML:", error);
      }

      return products;
    },

    // Parsear una línea de factura individual
    parseInvoiceLine: function (line, ns, lineId) {
      try {
        // Descripción del producto
        var description = "";
        var descNode =
          line.querySelector("Description, cbc\\:Description") ||
          line.getElementsByTagNameNS(ns.cbc, "Description")[0];
        if (descNode) {
          description = descNode.textContent.trim();
        }

        // Si no hay descripción, intentar obtener del Item
        if (!description) {
          var itemDesc =
            line.querySelector("Item Description, Item cbc\\:Description") ||
            line.querySelector("cac\\:Item cbc\\:Description");
          if (itemDesc) {
            description = itemDesc.textContent.trim();
          }
        }

        // Cantidad
        var quantity = 1;
        var qtyNode =
          line.querySelector("InvoicedQuantity, cbc\\:InvoicedQuantity") ||
          line.getElementsByTagNameNS(ns.cbc, "InvoicedQuantity")[0];
        if (qtyNode) {
          quantity = parseFloat(qtyNode.textContent) || 1;
        }

        // Precio unitario
        var unitPrice = 0;
        var priceNode =
          line.querySelector("Price PriceAmount, Price cbc\\:PriceAmount") ||
          line.querySelector("cac\\:Price cbc\\:PriceAmount");
        if (!priceNode) {
          var priceContainer =
            line.querySelector("Price, cac\\:Price") ||
            line.getElementsByTagNameNS(ns.cac, "Price")[0];
          if (priceContainer) {
            priceNode =
              priceContainer.querySelector("PriceAmount, cbc\\:PriceAmount") ||
              priceContainer.getElementsByTagNameNS(ns.cbc, "PriceAmount")[0];
          }
        }
        if (priceNode) {
          unitPrice = parseFloat(priceNode.textContent) || 0;
        }

        // Total de línea
        var lineTotal = 0;
        var lineExtNode =
          line.querySelector("LineExtensionAmount, cbc\\:LineExtensionAmount") ||
          line.getElementsByTagNameNS(ns.cbc, "LineExtensionAmount")[0];
        if (lineExtNode) {
          lineTotal = parseFloat(lineExtNode.textContent) || 0;
        }

        // Si no hay precio unitario pero hay total, calcular
        if (unitPrice === 0 && lineTotal > 0 && quantity > 0) {
          unitPrice = lineTotal / quantity;
        }

        // Referencia del vendedor
        var reference = "";
        var sellerIdNode =
          line.querySelector(
            "SellersItemIdentification ID, SellersItemIdentification cbc\\:ID"
          ) ||
          line.querySelector("cac\\:SellersItemIdentification cbc\\:ID");
        if (!sellerIdNode) {
          var sellerIdContainer =
            line.querySelector(
              "SellersItemIdentification, cac\\:SellersItemIdentification"
            ) ||
            line.getElementsByTagNameNS(ns.cac, "SellersItemIdentification")[0];
          if (sellerIdContainer) {
            sellerIdNode =
              sellerIdContainer.querySelector("ID, cbc\\:ID") ||
              sellerIdContainer.getElementsByTagNameNS(ns.cbc, "ID")[0];
          }
        }
        if (sellerIdNode) {
          reference = sellerIdNode.textContent.trim();
        }

        // Impuesto
        var taxPercent = 19; // Default IVA Colombia
        var taxAmount = 0;
        var taxTotalNode =
          line.querySelector("TaxTotal, cac\\:TaxTotal") ||
          line.getElementsByTagNameNS(ns.cac, "TaxTotal")[0];
        if (taxTotalNode) {
          var taxAmountNode =
            taxTotalNode.querySelector("TaxAmount, cbc\\:TaxAmount") ||
            taxTotalNode.getElementsByTagNameNS(ns.cbc, "TaxAmount")[0];
          if (taxAmountNode) {
            taxAmount = parseFloat(taxAmountNode.textContent) || 0;
          }

          var taxPercentNode =
            taxTotalNode.querySelector("Percent, cbc\\:Percent") ||
            taxTotalNode.getElementsByTagNameNS(ns.cbc, "Percent")[0];
          if (taxPercentNode) {
            taxPercent = parseFloat(taxPercentNode.textContent) || 19;
          }
        }

        // Calcular precio sugerido: costo + 35% redondeado al siguiente mil.
        var suggestedPrice = suggestSalePrice(unitPrice);
        var suggestedMargin =
          unitPrice > 0
            ? Math.round(((suggestedPrice - unitPrice) / unitPrice) * 100)
            : 35;

        // Detectar alertas
        var alerts = [];
        if (!description || description.trim() === "") {
          alerts.push("Descripción faltante");
        }
        if (unitPrice === 0) {
          alerts.push("Precio unitario es 0");
        }
        if (!reference || reference === "N/A") {
          alerts.push("Sin referencia del proveedor");
        }

        return {
          id: lineId,
          descripcion: description || "Producto sin descripción",
          referencia: reference || "N/A",
          cantidad: quantity,
          costo_unitario: unitPrice,
          total: lineTotal || unitPrice * quantity,
          categoria: this.detectCategory(description),
          grupo: "PRODUCTOS BÁSICOS",
          unidad: this.detectUnit(qtyNode),
          observaciones: "",
          precio_venta_sugerido: suggestedPrice,
          margen_sugerido: suggestedMargin,
          tax: taxPercent,
          tax_amount: taxAmount,
          alertas: alerts,
          requiere_validacion: alerts.length > 0,
        };
      } catch (error) {
        console.warn("Error parseando línea de factura:", error);
        return null;
      }
    },

    // Detectar categoría basada en la descripción
    detectCategory: function (description) {
      if (!description) return "OTROS";

      var desc = description.toUpperCase();

      // Mapeo simple de palabras clave a categorías
      var categoryMap = {
        "ALIMENTACIÓN Y BEBIDAS": [
          "ALIMENTO",
          "COMIDA",
          "BEBIDA",
          "CAFÉ",
          "AZUCAR",
          "ARROZ",
          "ACEITE",
          "LECHE",
        ],
        "SALUD Y BELLEZA": [
          "COSMETICO",
          "SHAMPOO",
          "JABON",
          "CREMA",
          "PERFUME",
          "LABIAL",
          "MAQUILLAJE",
        ],
        "HOGAR Y DECORACIÓN": [
          "HOGAR",
          "DECORACION",
          "MUEBLE",
          "COJIN",
          "CORTINA",
          "LAMPARA",
        ],
        LIMPIEZA: [
          "LIMPIEZA",
          "DETERGENTE",
          "DESINFECTANTE",
          "CLORO",
          "ESCOBA",
          "TRAPEADOR",
        ],
        "ELECTRÓNICA Y TECNOLOGÍA": [
          "ELECTRONICO",
          "CELULAR",
          "COMPUTADOR",
          "CABLE",
          "CARGADOR",
          "USB",
        ],
        "LIBROS Y PAPELERÍA": [
          "PAPEL",
          "CUADERNO",
          "LAPIZ",
          "BOLIGRAFO",
          "LIBRO",
          "CARPETA",
        ],
        "TEXTIL Y CONFECCIÓN": [
          "ROPA",
          "CAMISA",
          "PANTALON",
          "VESTIDO",
          "TELA",
          "HILO",
        ],
        "CALZADO Y COMPLEMENTOS": [
          "ZAPATO",
          "SANDALIA",
          "TENIS",
          "BOTA",
          "BOLSO",
          "CARTERA",
        ],
      };

      for (var category in categoryMap) {
        var keywords = categoryMap[category];
        for (var i = 0; i < keywords.length; i++) {
          if (desc.indexOf(keywords[i]) !== -1) {
            return category;
          }
        }
      }

      return "OTROS";
    },

    // Detectar unidad de medida desde el nodo de cantidad
    detectUnit: function (qtyNode) {
      if (!qtyNode) return "UND";

      var unitCode = qtyNode.getAttribute("unitCode") || "";
      var unitMap = {
        EA: "UND",
        UN: "UND",
        UND: "UND",
        KGM: "KG",
        KG: "KG",
        GRM: "GR",
        GR: "GR",
        LTR: "LT",
        LT: "LT",
        MLT: "ML",
        ML: "ML",
        MTR: "MT",
        MT: "MT",
        CMT: "CM",
        CM: "CM",
        PR: "PAR",
        PAR: "PAR",
        SET: "SET",
        BX: "CAJA",
        CAJA: "CAJA",
        PK: "PACK",
        PACK: "PACK",
      };

      return unitMap[unitCode.toUpperCase()] || "UND";
    },

    // Extraer información general del documento
    extractDocumentInfoFromXml: function (xmlDoc) {
      var docInfo = {
        document_number: "",
        date: "",
        currency: "COP",
        total_amount: 0,
        processing_time_ms: 0,
        processed_at: new Date().toISOString(),
        source: "XML",
      };

      try {
        var ns = {
          cbc: "urn:oasis:names:specification:ubl:schema:xsd:CommonBasicComponents-2",
        };

        // Número de factura
        var idNode =
          xmlDoc.querySelector("ParentDocumentID, cbc\\:ParentDocumentID") ||
          xmlDoc.getElementsByTagNameNS(ns.cbc, "ParentDocumentID")[0];
        if (!idNode) {
          idNode =
            xmlDoc.querySelector("ID, cbc\\:ID") ||
            xmlDoc.getElementsByTagNameNS(ns.cbc, "ID")[0];
        }
        if (idNode) {
          docInfo.document_number = idNode.textContent.trim();
        }

        // Fecha de emisión
        var dateNode =
          xmlDoc.querySelector("IssueDate, cbc\\:IssueDate") ||
          xmlDoc.getElementsByTagNameNS(ns.cbc, "IssueDate")[0];
        if (dateNode) {
          docInfo.date = dateNode.textContent.trim();
        }

        // Moneda
        var currencyNode =
          xmlDoc.querySelector(
            "DocumentCurrencyCode, cbc\\:DocumentCurrencyCode"
          ) ||
          xmlDoc.getElementsByTagNameNS(ns.cbc, "DocumentCurrencyCode")[0];
        if (currencyNode) {
          docInfo.currency = currencyNode.textContent.trim();
        }

        // Total
        var totalNode =
          xmlDoc.querySelector("PayableAmount, cbc\\:PayableAmount") ||
          xmlDoc.getElementsByTagNameNS(ns.cbc, "PayableAmount")[0];
        if (totalNode) {
          docInfo.total_amount = parseFloat(totalNode.textContent) || 0;
        }
      } catch (error) {
        console.warn("Error extrayendo información del documento:", error);
      }

      return docInfo;
    },

    // Genera SKU/PLU/EAN deterministas via util compartido.
    // Mismo (categoria + referencia + descripcion) => mismos códigos siempre,
    // sin importar el flujo (Master, DocImport, MasterImport).
    updateProductCodes: function (product) {
      if (!product) return;
      if (!product.categoria && !product.descripcion) return;

      const codes = resolveCodes({
        categoria: product.categoria,
        referencia: product.referencia,
        descripcion: product.descripcion,
        ean: product.ean,
      });

      product.sku_sugerido = codes.sku;
      product.plu_sugerido = codes.plu;
      product.ean_sugerido = codes.ean;
    },

    // Actualizar estadísticas después del procesamiento
    updateStatsAfterProcessing: function (productCount, metadata) {
      this.stats.total_documents += 1;
      this.stats.total_products += productCount;
      this.stats.documents_this_month += 1;
      this.stats.products_this_month += productCount;

      // Actualizar tiempo promedio de procesamiento
      var newTime = (metadata.processing_time_ms || 0) / 1000;
      if (newTime > 0) {
        this.stats.avg_processing_time = (
          (this.stats.avg_processing_time * (this.stats.total_documents - 1) + newTime) /
          this.stats.total_documents
        ).toFixed(1);
      }
    },

    convertToBase64: function (file) {
      return new Promise(function (resolve, reject) {
        var reader = new FileReader();
        reader.onload = function () {
          resolve(reader.result);
        };
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });
    },

    // ========================================
    // IMPORTACIÓN TABULAR (Excel / CSV)
    // ========================================

    // Entry point: lee el archivo con xlsx, abre selector de hoja o salta directo
    // al mapeo, e intenta cargar plantilla del proveedor en paralelo.
    processTabularFile: function () {
      var self = this;
      if (!this.selectedSupplier) {
        this.showMessage("Selecciona un proveedor primero", "warning");
        return;
      }
      if (!this.selectedFile) {
        this.showMessage("Selecciona un archivo", "warning");
        return;
      }

      this.processing = true;
      this.processingStep = 1;
      this.processingMessage = "Leyendo archivo...";
      this.processingDetail = "Parseando contenido " +
        (this.isExcelFile ? "Excel" : "CSV");

      this.readWorkbookFromFile(this.selectedFile)
        .then(function (workbook) {
          self.loadedWorkbook = workbook;
          self.availableSheets = workbook.SheetNames.slice();
          self.sheetRowCounts = {};
          self.availableSheets.forEach(function (name) {
            var ws = workbook.Sheets[name];
            var ref = ws && ws["!ref"] ? XLSX.utils.decode_range(ws["!ref"]) : null;
            self.sheetRowCounts[name] = ref ? ref.e.r - ref.s.r : 0;
          });

          self.processingStep = 2;

          if (self.availableSheets.length > 1) {
            self.selectedSheetName = self.availableSheets[0];
            self.processing = false;
            self.sheetSelectorDialog = true;
          } else {
            self.selectedSheetName = self.availableSheets[0];
            self.loadSheetIntoMapping();
          }
        })
        .catch(function (err) {
          self.processing = false;
          self.showMessage("No se pudo leer el archivo: " + err.message, "error");
        });
    },

    readWorkbookFromFile: function (file) {
      return new Promise(function (resolve, reject) {
        var reader = new FileReader();
        reader.onerror = function () {
          reject(new Error("Lectura del archivo falló"));
        };
        reader.onload = function (e) {
          try {
            var data = new Uint8Array(e.target.result);
            var wb = XLSX.read(data, { type: "array", cellDates: true });
            resolve(wb);
          } catch (err) {
            reject(err);
          }
        };
        reader.readAsArrayBuffer(file);
      });
    },

    confirmSheetSelection: function () {
      this.sheetSelectorDialog = false;
      this.processing = true;
      this.processingStep = 2;
      this.processingMessage = "Procesando hoja...";
      this.loadSheetIntoMapping();
    },

    // Convierte la hoja activa a headers + rows, sugiere mapeo y abre el dialog.
    loadSheetIntoMapping: function () {
      var self = this;
      try {
        var ws = this.loadedWorkbook.Sheets[this.selectedSheetName];
        if (!ws) throw new Error("Hoja no encontrada: " + this.selectedSheetName);

        // header:1 => primera fila = headers, resto = arrays paralelos.
        var matrix = XLSX.utils.sheet_to_json(ws, {
          header: 1,
          defval: "",
          raw: false,
          blankrows: false,
        });

        if (!matrix.length) {
          this.processing = false;
          this.showMessage("La hoja está vacía", "warning");
          return;
        }

        var rawHeaders = matrix[0].map(function (h) {
          return (h === null || h === undefined ? "" : String(h)).trim();
        });

        // Filtrar columnas con header vacío conservando el índice original.
        var keepIndices = [];
        var headers = [];
        rawHeaders.forEach(function (h, idx) {
          if (h !== "") {
            keepIndices.push(idx);
            headers.push(h);
          }
        });

        if (!headers.length) {
          this.processing = false;
          this.showMessage(
            "No se detectaron encabezados en la primera fila",
            "warning"
          );
          return;
        }

        var rows = matrix.slice(1).map(function (row) {
          return keepIndices.map(function (i) {
            return row[i] === undefined || row[i] === null ? "" : row[i];
          });
        });

        // Descartar filas totalmente vacías.
        rows = rows.filter(function (row) {
          return row.some(function (v) {
            return String(v).trim() !== "";
          });
        });

        this.tabularHeaders = headers;
        this.tabularRows = rows;
        this.columnMapping = this.suggestColumnMapping(headers);
        this.loadedTemplateFromSupplier = false;
        this.saveTemplateChecked = true;

        // Intentar cargar plantilla del proveedor (no bloqueante).
        this.loadImportTemplate(this.selectedSupplier.codigo, function (mapping) {
          if (mapping && typeof mapping === "object") {
            // Aceptar solo claves que efectivamente existan en los headers.
            var merged = Object.assign({}, self.columnMapping);
            headers.forEach(function (h) {
              if (Object.prototype.hasOwnProperty.call(mapping, h)) {
                merged[h] = mapping[h];
              }
            });
            self.columnMapping = merged;
            self.loadedTemplateFromSupplier = true;
            self.showMessage(
              "Plantilla del proveedor cargada",
              "info",
              2500
            );
          }
        });

        this.processing = false;
        this.mappingDialog = true;
      } catch (err) {
        this.processing = false;
        this.showMessage("Error procesando la hoja: " + err.message, "error");
      }
    },

    // Heurística local: match exacto > inclusión > Levenshtein ≤ 2.
    suggestColumnMapping: function (headers) {
      var mapping = {};
      var self = this;
      headers.forEach(function (header) {
        var best = { field: "__ignore__", score: 0 };
        var norm = self.normalizeHeader(header);
        Object.keys(FIELD_SYNONYMS).forEach(function (field) {
          FIELD_SYNONYMS[field].forEach(function (syn) {
            var nSyn = self.normalizeHeader(syn);
            var score = 0;
            if (norm === nSyn) score = 100;
            else if (norm.indexOf(nSyn) !== -1 || nSyn.indexOf(norm) !== -1)
              score = 70;
            else {
              var d = self.levenshtein(norm, nSyn);
              if (d <= 2 && nSyn.length >= 3) score = 50 - d * 10;
            }
            if (score > best.score) {
              best = { field: field, score: score };
            }
          });
        });
        mapping[header] = best.field;
      });
      return mapping;
    },

    normalizeHeader: function (str) {
      if (str === null || str === undefined) return "";
      // Quita diacríticos vía NFD + filtrado del bloque combining marks (̀-ͯ).
      return String(str)
        .toLowerCase()
        .normalize("NFD")
        .replace(new RegExp("[\\u0300-\\u036f]", "g"), "")
        .replace(/[._\-:]/g, " ")
        .replace(/\s+/g, " ")
        .trim();
    },

    levenshtein: function (a, b) {
      if (a === b) return 0;
      if (!a.length) return b.length;
      if (!b.length) return a.length;
      var prev = new Array(b.length + 1);
      var curr = new Array(b.length + 1);
      for (var j = 0; j <= b.length; j++) prev[j] = j;
      for (var i = 1; i <= a.length; i++) {
        curr[0] = i;
        for (var k = 1; k <= b.length; k++) {
          var cost = a.charAt(i - 1) === b.charAt(k - 1) ? 0 : 1;
          curr[k] = Math.min(
            curr[k - 1] + 1,
            prev[k] + 1,
            prev[k - 1] + cost
          );
        }
        var tmp = prev;
        prev = curr;
        curr = tmp;
      }
      return prev[b.length];
    },

    // Primer valor no vacío de la columna `header`, recortado a 40 chars.
    sampleValueForHeader: function (header) {
      var colIdx = this.tabularHeaders.indexOf(header);
      if (colIdx < 0) return "";
      for (var r = 0; r < this.tabularRows.length; r++) {
        var v = this.tabularRows[r][colIdx];
        if (v !== null && v !== undefined && String(v).trim() !== "") {
          var s = String(v).trim();
          return s.length > 40 ? s.substring(0, 37) + "..." : s;
        }
      }
      return "";
    },

    // Convierte un subset de rows al shape AI-product que consume updateProductCodes.
    mapRowsToAIShape: function (rows) {
      var self = this;
      // Construir índice campo→colIdx a partir del mapping actual.
      var fieldToCol = {};
      this.tabularHeaders.forEach(function (header, idx) {
        var target = self.columnMapping[header];
        if (target && target !== "__ignore__" && !(target in fieldToCol)) {
          fieldToCol[target] = idx;
        }
      });

      var getField = function (row, field, fallback) {
        if (!(field in fieldToCol)) return fallback;
        var v = row[fieldToCol[field]];
        return v === undefined || v === null ? fallback : v;
      };

      var toNumber = function (v, fallback) {
        if (v === "" || v === null || v === undefined) return fallback;
        // Acepta "1.234,56" (es-CO) y "1,234.56" (en-US); preserva enteros simples.
        var s = String(v).trim().replace(/\s/g, "");
        // Si tiene "," y "." asumir formato europeo con miles "." → quitar puntos.
        if (s.indexOf(",") !== -1 && s.indexOf(".") !== -1) {
          s = s.replace(/\./g, "").replace(",", ".");
        } else if (s.indexOf(",") !== -1 && s.indexOf(".") === -1) {
          s = s.replace(",", ".");
        }
        var n = parseFloat(s);
        return isNaN(n) ? fallback : n;
      };

      return rows.map(function (row, idx) {
        var desc = String(getField(row, "descripcion", "") || "").trim();
        var ref = String(getField(row, "referencia", "") || "").trim();
        var qty = toNumber(getField(row, "cantidad", 1), 1);
        var unitCost = toNumber(getField(row, "costo_unitario", 0), 0);
        var total = toNumber(getField(row, "total", null), null);
        if (total === null) total = qty * unitCost;
        // Si no hay costo unitario explícito pero sí total y cantidad, derivar.
        if (unitCost === 0 && total > 0 && qty > 0) unitCost = total / qty;

        var unidad = String(getField(row, "unidad", "UND") || "UND")
          .toUpperCase()
          .trim();
        if (self.availableUnits.indexOf(unidad) === -1) unidad = "UND";

        var categoria = String(getField(row, "categoria", "") || "").trim();
        if (!categoria) categoria = self.detectCategory(desc);

        var grupo = String(getField(row, "grupo", "") || "").trim();
        if (!grupo) grupo = "PRODUCTOS BÁSICOS";

        var ean = String(getField(row, "ean", "") || "").trim();
        var obs = String(getField(row, "observaciones", "") || "").trim();
        var taxRaw = getField(row, "tax", null);
        var tax = taxRaw === null || taxRaw === undefined || taxRaw === ""
          ? 19
          : toNumber(taxRaw, 19);

        // Precio de venta: usar el del archivo si viene mapeado, si no calcular sugerido.
        var precioVentaRaw = getField(row, "precio_venta", null);
        var precioVentaArchivo =
          precioVentaRaw === null || precioVentaRaw === undefined || precioVentaRaw === ""
            ? null
            : toNumber(precioVentaRaw, null);
        var suggestedPrice =
          precioVentaArchivo !== null && precioVentaArchivo > 0
            ? precioVentaArchivo
            : suggestSalePrice(unitCost);
        var margen =
          unitCost > 0
            ? Math.round(((suggestedPrice - unitCost) / unitCost) * 100)
            : 35;

        var alerts = [];
        if (!desc) alerts.push("Descripción faltante");
        if (unitCost === 0) alerts.push("Precio unitario es 0");
        if (!ref) alerts.push("Sin referencia del proveedor");

        return {
          id: idx + 1,
          descripcion: desc || "Producto sin descripción",
          referencia: ref || "N/A",
          cantidad: qty,
          costo_unitario: unitCost,
          total: total,
          categoria: categoria,
          grupo: grupo,
          unidad: unidad,
          observaciones: obs,
          precio_venta_sugerido: suggestedPrice,
          margen_sugerido: margen,
          tax: tax,
          tax_amount: 0,
          ean: ean,
          alertas: alerts,
          requiere_validacion: alerts.length > 0,
        };
      });
    },

    // Cierra el wizard, transforma todas las filas, genera códigos y cae al
    // flujo estándar de tabla editable.
    applyTabularMapping: function () {
      if (!this.mappingHasRequiredFields) {
        this.showMessage(
          "Mapea al menos descripción, cantidad y costo unitario",
          "warning"
        );
        return;
      }

      var products = this.mapRowsToAIShape(this.tabularRows);
      var self = this;
      products.forEach(function (p) {
        self.updateProductCodes(p);
      });

      this.processingResults = products;
      this.documentInfo = {
        proveedor_id: this.selectedSupplier.codigo,
        currency: "COP",
        processing_time_ms: 0,
        processed_at: new Date().toISOString(),
        source: this.isExcelFile ? "EXCEL" : "CSV",
      };

      if (this.saveTemplateChecked) {
        this.saveImportTemplate(
          this.selectedSupplier.codigo,
          this.columnMapping
        );
      }

      this.mappingDialog = false;
      this.loadedWorkbook = null;
      this.availableSheets = [];
      this.tabularHeaders = [];
      this.tabularRows = [];

      this.showMessage(
        products.length + " productos importados desde archivo",
        "success",
        4000
      );
    },

    cancelTabularImport: function () {
      this.sheetSelectorDialog = false;
      this.mappingDialog = false;
      this.loadedWorkbook = null;
      this.availableSheets = [];
      this.selectedSheetName = null;
      this.tabularHeaders = [];
      this.tabularRows = [];
      this.columnMapping = {};
      this.loadedTemplateFromSupplier = false;
      this.processing = false;
    },

    // Lee plantilla de mapeo persistida para este proveedor. Tolera ausencia
    // de tabla en backend: degrada silenciosamente y el wizard sigue usando
    // la sugerencia heurística.
    loadImportTemplate: function (proveedorCodigo, cb) {
      var qry = {
        store: window.profile.store_id,
        table: "import_templates",
        filters: [
          {
            field: "store",
            operator: "=",
            value: window.profile.store_id.substring(0, 6),
          },
          { field: "id_proveedor", operator: "=", value: proveedorCodigo },
        ],
      };
      webserver(
        "get_dataTable",
        qry,
        function (data) {
          if (!data || data.error || !data.length) {
            cb(null);
            return;
          }
          var row = data[0];
          var raw = row.mapping_json || row.mapping || "";
          try {
            var parsed = typeof raw === "string" ? JSON.parse(raw) : raw;
            cb(parsed);
          } catch (e) {
            console.warn("Plantilla de mapeo corrupta:", e);
            cb(null);
          }
        },
        function () {
          cb(null);
        }
      );
    },

    saveImportTemplate: function (proveedorCodigo, mapping) {
      var qry = {
        store: window.profile.store_id,
        table: "import_templates",
        row: {
          store: window.profile.store_id.substring(0, 6),
          id_proveedor: proveedorCodigo,
          mapping_json: JSON.stringify(mapping),
          updated_at: new Date().toISOString(),
        },
      };
      webserver(
        "put_dataTable",
        qry,
        function () {
          // Silencioso: no interrumpir el flujo principal.
        },
        function (err) {
          console.warn("No se pudo guardar la plantilla de mapeo:", err);
        }
      );
    },

    // Quick action methods
    selectAllProducts: function () {
      this.selectedProducts = [...this.processingResults];
    },

    flagExpensiveProducts: function () {
      this.processingResults.forEach(function (product) {
        if (product.costo_unitario > 50000) {
          if (!product.alertas) product.alertas = [];
          if (product.alertas.indexOf("Producto de alto valor") === -1) {
            product.alertas.push("Producto de alto valor");
          }
        }
      });
      this.showMessage("Productos costosos marcados", "info");
    },

    reviewMissingData: function () {
      var missingCount = 0;
      this.processingResults.forEach(function (product) {
        var alerts = [];
        if (!product.descripcion || product.descripcion.trim() === "") {
          alerts.push("Descripción faltante");
        }
        if (!product.referencia || product.referencia === "N/A") {
          alerts.push("Referencia faltante");
        }
        if (product.costo_unitario <= 0) {
          alerts.push("Costo inválido");
        }

        if (alerts.length > 0) {
          product.alertas = alerts;
          missingCount++;
        }
      });

      this.showMessage(missingCount + " productos requieren atención", "warning");
    },

    autoFixPrices: function () {
      var fixedCount = 0;
      this.processingResults.forEach(function (product) {
        // Auto-corregir total si no coincide
        var expectedTotal = product.cantidad * product.costo_unitario;
        if (Math.abs(product.total - expectedTotal) > 0.01) {
          product.total = expectedTotal;
          fixedCount++;
        }
      });

      this.showMessage(fixedCount + " precios corregidos automáticamente", "success");
    },

    // Product editing - FORMULARIO COMPLETO
    editProduct: function (item) {
      this.editingProduct = Object.assign({}, item);
      this.editDialog = true;
    },

    updateEditTotals: function () {
      if (
        this.editingProduct &&
        this.editingProduct.cantidad &&
        this.editingProduct.costo_unitario
      ) {
        this.editingProduct.total =
          this.editingProduct.cantidad * this.editingProduct.costo_unitario;
        this.calculateMargin();
      }
    },

    calculateMargin: function () {
      if (
        this.editingProduct &&
        this.editingProduct.costo_unitario > 0 &&
        this.editingProduct.precio_venta_sugerido > 0
      ) {
        var margin =
          ((this.editingProduct.precio_venta_sugerido -
            this.editingProduct.costo_unitario) /
            this.editingProduct.costo_unitario) *
          100;
        this.editingProduct.margen_sugerido = Math.round(margin);
      }
    },

    saveProductEdit: function () {
      var self = this;

      if (!this.$refs.editForm.validate()) {
        this.showMessage("Por favor completa todos los campos requeridos", "warning");
        return;
      }

      this.savingEdit = true;

      // Simular guardado
      setTimeout(function () {
        if (self.editingProduct) {
          var index = self.processingResults.findIndex(
            (p) => p.id === self.editingProduct.id
          );
          if (index > -1) {
            // Actualizar totales antes de guardar
            self.editingProduct.total =
              self.editingProduct.cantidad * self.editingProduct.costo_unitario;

            // Reemplazar el producto en la lista
            self.processingResults.splice(index, 1, self.editingProduct);
          }
        }

        self.savingEdit = false;
        self.editDialog = false;
        self.editingProduct = null;
        self.showMessage("Producto actualizado exitosamente", "success");
      }, 1000);
    },

    duplicateProduct: function (item) {
      var newProduct = Object.assign({}, item);
      newProduct.id = Math.max(...this.processingResults.map((p) => p.id)) + 1;
      newProduct.descripcion += " (Copia)";
      this.processingResults.push(newProduct);
      this.showMessage("Producto duplicado", "info");
    },

    duplicateAndClose: function () {
      if (this.editingProduct) {
        this.duplicateProduct(this.editingProduct);
        this.editDialog = false;
        this.editingProduct = null;
      }
    },

    removeProduct: function (item) {
      var index = this.processingResults.findIndex(function (p) {
        return p.id === item.id;
      });
      if (index > -1) {
        this.processingResults.splice(index, 1);
        this.showMessage("Producto eliminado", "info");
      }
    },

    removeAndClose: function () {
      if (this.editingProduct) {
        this.removeProduct(this.editingProduct);
        this.editDialog = false;
        this.editingProduct = null;
      }
    },

    // Save products - SIMPLIFICADO PARA USAR ENDPOINTS EXISTENTES

    startNewProcess: function () {
      // Usar el método de reset completo
      this.resetFormCompleto();
    },

    /**
     * Buscar código de categoría desde el settings
     * @param {string} categoria - Valor de la categoría (ej: "COSMETICOS")
     * @returns {string} Código de la categoría (ej: "01")
     */
    // Fuente única para resolver categoría -> código.
    // Match por `valor` o `codigo`, case-insensitive. Default "99" (OTROS)
    // si no encuentra, consistente con el contrato del backend que espera código no vacío.
    // Delegado al util compartido: src/utils/skuPluGenerator.js
    getCategoriaCode: function (categoria) {
      return getCategoriaCodeUtil(categoria);
    },

    // Función para parsear productos AI al formato items
    parseProductsToItemsFormat() {
      const parsedItems = this.processingResults.map((aiProduct) => {
        // Buscar códigos de categoría y grupo

        const categoriaCode = this.getCategoriaCode(aiProduct.categoria);
        const grupoCode = this.getGroupCode(aiProduct.grupo);

        return {
          // Campos obligatorios
          id_pos: window.store.store_id,
          sku: aiProduct.sku_sugerido || "",
          ean: aiProduct.ean_sugerido || "",
          plu: aiProduct.plu_sugerido || "",

          // Clasificación (CÓDIGOS, no texto)
          categoria: categoriaCode,
          grupo: grupoCode,
          marca: "001", // Marca genérica por defecto

          // Información del producto
          refe: aiProduct.referencia || "",
          nombre: aiProduct.descripcion,
          color: this.getColorCode(aiProduct.observaciones) || "99", // MULTICOLOR por defecto
          uom: aiProduct.unidad || "UND",
          talla: this.extractSize(aiProduct.observaciones) || "U",

          // Precios y costos
          cantidad: parseFloat(aiProduct.cantidad) || 0,
          costo: parseFloat(aiProduct.costo_unitario) || 0,
          precio: parseFloat(aiProduct.precio_venta_sugerido) || 0,
          precioB: 0.0,
          precioC: 0.0,
          precioD: 0.0,

          // Información del proveedor
          id_proveedor: this.selectedSupplier.codigo || "",
          proveedor: this.selectedSupplier.nombre || "",
          cod_product_proveedor: aiProduct.referencia || "",

          // Fechas
          first_in: new Date().toISOString().split("T")[0],
          last_in: new Date().toISOString().split("T")[0],

          // Campos específicos de calzado (opcional)
          tacon: null,
          suela: null,
          msuela: null,
          mzapato: null,

          // Campos de control
          audit: 0,
          descuento: 0.0,
          status: 1, // Activo
          concepto: "INGRESO AI",
          modelo: null,
          tallas: aiProduct.observaciones || "",
          tax: 19.0,
          ean_brand: null,
          color_code: this.getColorCode(aiProduct.observaciones) || "99",
        };
      });

      return parsedItems;
    },

    // Métodos auxiliares para mapeo de códigos
    getGroupCode(groupName) {
      const settings = window.settings || {};
      const grupo = settings.GRUPO?.find((grp) => grp.valor === groupName);
      return grupo?.codigo || "01"; // PRODUCTOS BÁSICOS por defecto
    },

    getColorCode(observaciones) {
      if (!observaciones) return "99";

      const colorMap = {
        BLANCO: "01",
        NEGRO: "02",
        GRIS: "03",
        AZUL: "04",
        ROJO: "05",
        VERDE: "06",
        AMARILLO: "07",
        NARANJA: "08",
        ROSA: "09",
        VIOLETA: "10",
        MARRÓN: "11",
        BEIGE: "12",
      };

      const obs = observaciones.toUpperCase();
      for (const [color, code] of Object.entries(colorMap)) {
        if (obs.includes(color)) return code;
      }

      return "99"; // MULTICOLOR por defecto
    },

    extractSize(observaciones) {
      if (!observaciones) return "U";

      const sizeRegex = /\b(XS|S|M|L|XL|XXL|\d+)\b/i;
      const match = observaciones.match(sizeRegex);
      return match ? match[0].toUpperCase() : "U";
    },

    // Función final para crear la orden - CORREGIDA
    createPurchaseOrder: function (data) {
      var self = this;
      var parsedItems = data;

      // 🆕 CREAR ARRAY MASTER - Solo cambiar id_pos a primeros 6 caracteres
      self.masterItems = parsedItems.map(function (item) {
        var masterItem = Object.assign({}, item); // Copia completa del item
        masterItem.id_pos = item.id_pos.substring(0, 6); // Solo cambiar id_pos
        return masterItem;
      });

      // Usa el endpoint existente `new_product` (InventoryService::newProduct),
      // que hace bulk upsert en items con ON DUPLICATE KEY UPDATE.
      // El endpoint toma el id_pos del primer item para fijar la BD del tenant.
      var qry = { data: self.masterItems };

      console.log("🚀 Creando productos en maestro:", qry);

      webserver(
        "new_product",
        qry,
        function (response) {
          self.savingProducts = false;

          // newProduct devuelve {affected: <n>}; tratamos error solo si viene flag explicito.
          if (response && response.error !== true) {
            var affected = response && response.affected !== undefined ? response.affected : self.masterItems.length;
            self.showMessage(
              "✅ " + affected + " producto(s) creados/actualizados en el maestro",
              "success",
              5000
            );
            self.orderDocument = response;
            // new_product no crea documento; no mostramos viewer.
            self.showOrderDocument = false;

            self.resetFormCompleto();
            self.updateStatsAfterOrder();
          } else {
            self.showMessage(
              "❌ Error al crear productos: " + (response?.message || response?.error || "Error desconocido"),
              "error"
            );
          }
        },
        function (error) {
          self.savingProducts = false;
          self.showMessage("❌ Error de conexión: " + error, "error");
        }
      );
    },

    // 📊 Actualizar estadísticas después de crear orden
    updateStatsAfterOrder: function () {
      console.log("📊 Actualizando estadísticas después de crear orden...");

      // Incrementar contadores locales inmediatamente
      this.stats.total_documents += 1;
      this.stats.documents_this_month += 1;

      // Opcional: Recargar estadísticas reales del servidor
      setTimeout(() => {
        this.loadRealStats();
      }, 1000); // Esperar 1 segundo para que se procese en el servidor
    },

    // 🔄 RESET COMPLETO del formulario después de crear orden
    resetFormCompleto: function () {
      console.log("🔄 Reseteando formulario completo...");

      // 1️⃣ Limpiar archivos y previews
      this.selectedFile = null;
      this.imagePreview = null;
      this.dragOver = false;
      if (this.$refs.fileInput) {
        this.$refs.fileInput.value = "";
      }

      // 2️⃣ Limpiar supplier y documentos
      this.selectedSupplier = null;
      this.documentType = "invoice";
      this.motive = null;

      // 3️⃣ Limpiar estados de procesamiento
      this.processing = false;
      this.processingStep = 1;
      this.processingMessage = "Preparando imagen...";
      this.processingDetail = "";
      this.savingProducts = false;

      // 4️⃣ Limpiar resultados y datos
      this.processingResults = [];
      this.documentInfo = {};
      this.selectedProducts = [];

      // 5️⃣ Limpiar dialogs de edición
      this.editDialog = false;
      this.editingProduct = null;
      this.editFormValid = false;
      this.savingEdit = false;

      // 5.1️⃣ Limpiar wizard de importación tabular
      this.sheetSelectorDialog = false;
      this.mappingDialog = false;
      this.loadedWorkbook = null;
      this.availableSheets = [];
      this.selectedSheetName = null;
      this.tabularHeaders = [];
      this.tabularRows = [];
      this.columnMapping = {};
      this.loadedTemplateFromSupplier = false;

      // 6️⃣ Resetear formularios si existen
      if (this.$refs.editForm) {
        this.$refs.editForm.reset();
        this.$refs.editForm.resetValidation();
      }

      // 7️⃣ Forzar reactividad
      var self = this;
      this.$nextTick(function () {
        self.showMessage("📝 Formulario listo para nuevo documento", "info", 3000);
      });
    },
    executeFullProcess() {
      if (!this.canSaveProducts) {
        this.showMessage(
          "Completa todos los productos antes de crear ingreso",
          "warning"
        );
        return;
      }

      this.savingProducts = true;

      try {
        // 1. Parsear productos al formato items
        const parsedItems = this.parseProductsToItemsFormat();

        // 2. Crear la orden/entrada
        this.createPurchaseOrder(parsedItems);
      } catch (error) {
        this.savingProducts = false;
        this.showMessage("Error en el procesamiento: " + error.message, "error");
      }
    },
  },

  // En el formulario de edición, agregar watchers
  watch: {
    "editingProduct.referencia": function () {
      if (this.editingProduct) {
        this.updateProductCodes(this.editingProduct);
      }
    },

    "editingProduct.descripcion": function () {
      if (this.editingProduct) {
        this.updateProductCodes(this.editingProduct);
      }
    },

    "editingProduct.categoria": function () {
      if (this.editingProduct) {
        this.updateProductCodes(this.editingProduct);
      }
    },
  },
};
</script>

<style scoped>
/* Estilos existentes + nuevos para formulario */
.kpi-card {
  border: 1px solid #e0e0e0;
  transition: all 0.3s ease;
}

.kpi-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08) !important;
}

.kpi-card .v-card__text {
  display: flex;
  align-items: center;
  gap: 16px;
}

.kpi-icon {
  background-color: #f5f5f5;
  border-radius: 12px;
  padding: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.kpi-icon .v-icon {
  font-size: 28px !important;
}

.kpi-content {
  flex: 1;
}

.kpi-label {
  font-size: 0.875rem;
  color: #666;
  font-weight: 500;
  margin-bottom: 4px;
}

.kpi-value {
  font-size: 1.75rem;
  font-weight: 700;
  color: #333;
  line-height: 1.2;
}

.kpi-subtitle {
  font-size: 0.75rem;
  color: #999;
  margin-top: 4px;
}

.main-card {
  border: 1px solid #e0e0e0;
}

.minimal-toolbar {
  border-bottom: 1px solid #e0e0e0;
  padding: 16px 24px;
}

.section-title {
  font-size: 1.5rem;
  font-weight: 600;
  color: #333;
}

.upload-area {
  border: 3px dashed #bdc3c7;
  border-radius: 12px;
  padding: 40px 20px;
  text-align: center;
  cursor: pointer;
  transition: all 0.3s ease;
  background: #f8f9fa;
  min-height: 300px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.upload-area:hover:not(.disabled),
.upload-area.drag-over:not(.disabled) {
  border-color: #3498db;
  background: #e3f2fd;
  transform: scale(1.02);
}

.upload-area.has-file {
  cursor: default;
  padding: 20px;
}

.upload-area.disabled {
  cursor: not-allowed;
  background: #f0f0f0;
  border-color: #ccc;
}

.upload-content {
  color: #666;
}

.file-preview {
  display: flex;
  gap: 20px;
  align-items: center;
  width: 100%;
  position: relative;
}

.preview-image-container {
  flex: 0 0 200px;
  position: relative;
}

.preview-image {
  max-width: 200px;
  max-height: 200px;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.preview-xml-container {
  flex: 0 0 200px;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #e8f5e9;
  border-radius: 12px;
  padding: 30px;
  min-height: 150px;
}

.file-info {
  flex: 1;
  text-align: left;
}

.minimal-table >>> thead tr th {
  background-color: #fafafa !important;
  font-weight: 600 !important;
  color: #666 !important;
  border-bottom: 2px solid #e0e0e0 !important;
  font-size: 0.875rem !important;
}

.minimal-table >>> tbody tr:hover {
  background-color: #f5f5f5 !important;
}

.clickable-rows >>> tbody tr {
  cursor: pointer;
}

.clickable-rows >>> tbody tr:hover {
  background-color: #e3f2fd !important;
}

/* Nuevos estilos para celdas read-only */
.product-cell {
  display: flex;
  align-items: center;
  gap: 8px;
}

.product-text {
  font-size: 0.875rem;
  color: #333;
}

.document-info p {
  margin-bottom: 8px;
  color: #666;
}

.financial-summary {
  background: #f5f5f5;
  border-radius: 8px;
  padding: 16px;
}

.summary-item {
  text-align: center;
}

.summary-label {
  font-size: 0.875rem;
  color: #666;
  margin-bottom: 4px;
}

.summary-value {
  font-size: 1.25rem;
  font-weight: 600;
  color: #333;
}

.results-info {
  display: flex;
  align-items: center;
}

.action-buttons {
  display: flex;
  gap: 4px;
}

.gap-2 > * {
  margin-right: 8px;
  margin-bottom: 8px;
}

@media (max-width: 960px) {
  .file-preview {
    flex-direction: column;
    text-align: center;
  }

  .preview-image-container {
    flex: none;
  }

  .minimal-toolbar {
    flex-wrap: wrap;
    gap: 16px;
  }

  .kpi-card .v-card__text {
    flex-direction: column;
    text-align: center;
  }

  .action-buttons {
    flex-direction: column;
  }
}
</style>
